import { useState, useEffect, useMemo } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {LogOut, Eye, Trash2, Download, Copy, MessageCircleDashed as MessageCircle, Users, Plus, Link as LinkIcon, Power, PowerOff, User as UserIcon, Shield, AlertTriangle, Crown, Package, Megaphone, KeyRound, Mail, X, Bell, Award, Settings, Baby, Calculator, FileText, GraduationCap, Building2, UserPlus, Sparkles, Lock, HeartHandshake, Rocket} from 'lucide-react'
import DivulgarLink from '@/components/DivulgarLink'
import AbaInscritos from '@/components/AbaInscritos'
import AbaCursosAromaterapia from '@/components/AbaCursosAromaterapia'
import AbaCalculadoraOleosEssenciais from '@/components/AbaCalculadoraOleosEssenciais'
import AbaProtocolosOleosEssenciais from '@/components/AbaProtocolosOleosEssenciais'
import LinkMateriaisConfig from '@/components/LinkMateriaisConfig'
import PerfilConsultora from '@/pages/PerfilConsultora'
import MeuPerfilAba from '@/components/MeuPerfilAba'
import AbaTreinamento from '@/components/AbaTreinamento'
import AbaEmpresas from '@/components/AbaEmpresas'
import AbaVivencias from '@/components/AbaVivencias'
import AbaComoCrescer from '@/components/AbaComoCrescer'
import AbaMentoria from '@/components/AbaMentoria'
import TrocaSenhaObrigatoria from '@/components/TrocaSenhaObrigatoria'
import { OLEOS_INFO } from '@/types/diagnostico'
import AdminModalTabs from '@/components/AdminModalTabs'
import ScriptVendas from '@/components/ScriptVendas'
import ReferenciaRapidaOleos from '@/components/ReferenciaRapidaOleos'
import BlocoProtocoloUso from '@/components/BlocoProtocoloUso'
import NotasObservacoes from '@/components/NotasObservacoes'
import {
  LINK_HOTMART,
  acessoLiberado,
  calcularDataExpiracao,
  coresStatusAssinatura,
  diasRestantes,
  formatarData,
  rotuloStatus,
  temAcessoEmpresas,
  type StatusAssinatura,
  type TipoAcesso,
  type PlanoAssinatura,
} from '@/utils/assinatura'
import { copiarTexto } from '@/utils/clipboard'

// Senha da administradora principal
const SENHA_ADMIN = 'eusousuprida520'

// URL da Deno Function de envio de e-mails
const EMAIL_FUNCTION_URL = 'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

// Nome oficial dōTERRA de cada mix, exibido entre parênteses junto ao nome do mix
// (mesmo padrão usado na mensagem pronta gerada em ScriptVendas.tsx)
const NOME_DOTERRA_MIX: Record<string, string> = {
  'Mix para Estabilidade': 'Balance',
  'Mix para Tensão e Dores': 'Deep Blue',
  'Mix Imunidade': 'On Guard',
  'Mix Mensal Feminino': 'ClaryCalm',
  'Mix Respiratório': 'Breathe',
  'Mix Digestivo': 'ZenGest',
  'Mix Metabólico': 'MetaPWR',
}

// Retorna "Nome do mix (Nome dōTERRA)" quando for um mix mapeado; caso contrário retorna o nome original
const nomeExibicaoOleo = (oleo: string): string => {
  const nomeDoterra = NOME_DOTERRA_MIX[oleo]
  return nomeDoterra ? `${oleo} (${nomeDoterra})` : oleo
}

// Funcao para decodificar strings Unicode escapadas
const decodeUnicodeString = (str: any): any => {
  if (typeof str === 'string') {
    try {
      if (str.includes('\\u')) {
        return JSON.parse(`"${str}"`)
      }
      return str
    } catch {
      return str
    }
  }
  if (Array.isArray(str)) {
    return str.map(item => decodeUnicodeString(item))
  }
  if (str && typeof str === 'object') {
    const decoded: any = {}
    for (const key in str) {
      decoded[key] = decodeUnicodeString(str[key])
    }
    return decoded
  }
  return str
}

// Tipos
interface Consultora {
  _id: string
  nome: string
  usuario: string
  senha: string
  email?: string
  whatsapp?: string
  ativo: boolean
  tipoAcesso?: TipoAcesso
  statusAssinatura?: StatusAssinatura
  planoAssinatura?: PlanoAssinatura
  dataExpiracao?: string
  distribuidora?: string
  migradoLegado?: boolean
  precisaTrocarSenha?: boolean
  primeiroAcesso?: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

interface Distribuidora {
  _id: string
  nome: string
  slug: string
  email?: string
  whatsapp?: string
  senha: string
  totalAcessos: number
  ativo: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

// Retorna a origem do cadastro de uma consultora para exibição na aba Gestão de Acessos
const origemConsultora = (c: Consultora): string => {
  if (c.tipoAcesso === 'capacitacao') return 'Curso/Capacitação'
  if (c.creator === 'self') return 'Autocadastro'
  if (c.creator === 'admin') return 'Cadastro manual'
  return c.creator || '—'
}

const gerarSenhaProvisoria = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let senha = ''
  const bytes = new Uint8Array(10)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
    for (let i = 0; i < 10; i++) senha += chars[bytes[i] % chars.length]
  } else {
    for (let i = 0; i < 10; i++) senha += chars[Math.floor(Math.random() * chars.length)]
  }
  if (!/\d/.test(senha)) senha = `${senha.slice(0, 9)}7`
  return senha
}

const coresOrigemConsultora = (origem: string): { bg: string; text: string } => {
  if (origem === 'Autocadastro') return { bg: '#FEF3C7', text: '#92400E' }
  if (origem === 'Curso/Capacitação') return { bg: '#E6F1FB', text: '#185FA5' }
  if (origem === 'Cadastro manual') return { bg: '#D8F3DC', text: '#2D6A4F' }
  return { bg: '#F3F4F6', text: '#4B5563' }
}

type PerfilLogado =
  | { tipo: 'admin' }
  | { tipo: 'consultora'; usuario: string; nome: string; _id: string }

type AbaAdmin = 'leads' | 'leads_kids' | 'consultoras' | 'acessos' | 'distribuidoras' | 'divulgar' | 'calculadora' | 'protocolos' | 'perfil' | 'configuracoes' | 'treinamento' | 'empresas' | 'inscritos' | 'vivencias' | 'como_crescer' | 'mentoria'

// NOTE: O antigo componente interno AbaCapacitacao foi totalmente removido.
// A gestão de inscritos agora é feita exclusivamente pela aba
// "Inscritas na Capacitação" via o componente AbaInscritos.

/* eslint-disable @typescript-eslint/no-unused-vars */
function __AbaCapacitacaoRemovida_NAO_USAR(_props: { lumi: any; consultoras: any[]; isAdmin: boolean; consultoraLogada: string | null }) {
  const consultoras = _props.consultoras
  const isAdminProp = _props.isAdmin
  const consultoraLogadaProp = _props.consultoraLogada
  const [inscritos, setInscritos] = useState<any[]>([])
  const [carregando, setCarregando] = useState(true)
  const [busca, setBusca] = useState('')
  const [filtroEtapa, setFiltroEtapa] = useState('todos')
  const [filtroIndicado, setFiltroIndicado] = useState('todos')
  const [selecionado, setSelecionado] = useState<any | null>(null)
  const [salvandoId, setSalvandoId] = useState<string | null>(null)
  const [confirmarDelete, setConfirmarDelete] = useState<any | null>(null)
  const [deletando, setDeletando] = useState(false)

  useEffect(() => {
    void carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function carregar() {
    setCarregando(true)
    try {
      const resp = await lumi.entities.capacitacao.list({ sort: { dataInscricao: -1 }, limit: 500 })
      const lista = resp?.list ?? []
      // Consultora vê apenas inscritas que ela indicou
      if (!isAdminProp && consultoraLogadaProp) {
        setInscritos(lista.filter((r: any) => r.indicadoPor === consultoraLogadaProp))
      } else {
        setInscritos(lista)
      }
    } catch (e) {
      console.error('[capacitacao] erro ao carregar', e)
    } finally {
      setCarregando(false)
    }
  }

  function nomeConsultora(indicadoPor: string): string {
    if (!indicadoPor) return '—'
    const c = consultoras.find(x => x.usuario === indicadoPor)
    return c ? c.nome : indicadoPor
  }

  function labelEtapa(etapa: string): string {
    const map: Record<string, string> = {
      selecao_concluida: 'Inscrição concluída',
      diagnostico_concluido: 'Diagnóstico preenchido',
      aula1: 'Em andamento — aula 1/7',
      aula2: 'Em andamento — aula 2/7',
      aula3: 'Em andamento — aula 3/7',
      aula4: 'Em andamento — aula 4/7',
      aula5: 'Em andamento — aula 5/7',
      aula6: 'Em andamento — aula 6/7',
      aula7: 'Em andamento — aula 7/7',
      certificado_emitido: 'Curso concluído',
      acesso_criado: 'Acesso criado',
    }
    return map[etapa] ?? etapa
  }

  function corEtapa(etapa: string): { bg: string; text: string } {
    if (etapa === 'certificado_emitido' || etapa === 'acesso_criado') return { bg: '#D8F3DC', text: '#2D6A4F' }
    if (etapa?.startsWith('aula')) return { bg: '#E6F1FB', text: '#185FA5' }
    if (etapa === 'diagnostico_concluido') return { bg: '#FEF3C7', text: '#92400E' }
    return { bg: '#F3F4F6', text: '#4B5563' }
  }

  function diasRestantesAcesso(reg: any): number | null {
    if (!reg?.dataCertificado) return null
    const fim = new Date(reg.dataCertificado).getTime() + 15 * 24 * 60 * 60 * 1000
    return Math.ceil((fim - Date.now()) / 86400000)
  }

  async function liberarAcessoPermanente(reg: any) {
    if (!confirm(`Liberar acesso gratuito permanente para ${reg.nome}?`)) return
    setSalvandoId(reg._id)
    try {
      // Criar/atualizar consultora com statusAssinatura gratuito
      const consultoraExist = await lumi.entities.consultoras.list({ filter: { email: reg.email }, limit: 1 })
      if ((consultoraExist?.list ?? []).length > 0) {
        const c = consultoraExist.list[0]
        await lumi.entities.consultoras.update(c._id, {
          statusAssinatura: 'gratuito',
          tipoAcesso: 'gratuito',
          dataExpiracao: '',
          ativo: true,
          updatedAt: new Date().toISOString(),
        })
      } else {
        const usuario = (reg.nome ?? '').toLowerCase().replace(/\s+/g, '').slice(0, 20) + Math.floor(Math.random() * 100)
        await lumi.entities.consultoras.create({
          nome: reg.nome,
          email: reg.email,
          whatsapp: reg.whatsapp,
          usuario,
          senha: Math.random().toString(36).slice(2, 10),
          ativo: true,
          tipoAcesso: 'gratuito',
          statusAssinatura: 'gratuito',
          dataExpiracao: '',
          cursoConcluido: true,
          exibirOleosResultado: false,
          exibirOleosResultadoKids: false,
          indicadoPor: reg.indicadoPor ?? '',
          migradoLegado: false,
          enviouAvisoVencimento3Dias: false,
          creator: 'admin',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      }
      await lumi.entities.capacitacao.update(reg._id, {
        etapaAtual: 'acesso_criado',
        updatedAt: new Date().toISOString(),
      })
      toast.success(`Acesso liberado para ${reg.nome}!`)
      await carregar()
    } catch (e) {
      console.error('[capacitacao] erro ao liberar acesso', e)
      toast.error('Erro ao liberar acesso')
    } finally {
      setSalvandoId(null)
    }
  }

  async function estenderAcesso(reg: any, dias: number) {
    const base = reg.dataCertificado ? new Date(reg.dataCertificado) : new Date()
    const novaExpiracao = new Date(base.getTime() + dias * 24 * 60 * 60 * 1000).toISOString()
    setSalvandoId(reg._id)
    try {
      await lumi.entities.capacitacao.update(reg._id, {
        dataCertificado: novaExpiracao,
        updatedAt: new Date().toISOString(),
      })
      toast.success(`Acesso estendido por ${dias} dias!`)
      await carregar()
    } catch (e) {
      console.error('[capacitacao] erro ao estender acesso', e)
      toast.error('Erro ao estender acesso')
    } finally {
      setSalvandoId(null)
    }
  }

  async function deletarInscrito(reg: any) {
    setDeletando(true)
    try {
      // Remover da coleção capacitacao
      await lumi.entities.capacitacao.delete(reg._id)
      // Verificar e remover conta de consultora com statusAssinatura 'capacitacao' vinculada ao mesmo whatsapp/email
      try {
        const filtros = []
        if (reg.email) filtros.push({ email: reg.email })
        if (reg.whatsapp) filtros.push({ whatsapp: reg.whatsapp })
        for (const filtro of filtros) {
          const resp = await lumi.entities.consultoras.list({ filter: filtro, limit: 5 })
          for (const c of (resp?.list ?? [])) {
            if (c.statusAssinatura === 'capacitacao') {
              await lumi.entities.consultoras.delete(c._id)
            }
          }
        }
      } catch (e) {
        console.warn('[capacitacao] nao foi possivel verificar consultoras vinculadas', e)
      }
      toast.success(`${reg.nome} removido(a) da seleção.`)
      setConfirmarDelete(null)
      await carregar()
    } catch (e) {
      console.error('[capacitacao] erro ao deletar inscrito', e)
      toast.error('Erro ao deletar. Tente novamente.')
    } finally {
      setDeletando(false)
    }
  }

  async function encerrarAcesso(reg: any) {
    if (!confirm(`Encerrar acesso de ${reg.nome}?`)) return
    setSalvandoId(reg._id)
    try {
      await lumi.entities.capacitacao.update(reg._id, {
        etapaAtual: 'selecao_concluida',
        dataCertificado: '',
        certificadoEmitido: false,
        updatedAt: new Date().toISOString(),
      })
      toast.success(`Acesso de ${reg.nome} encerrado.`)
      await carregar()
    } catch (e) {
      console.error('[capacitacao] erro ao encerrar acesso', e)
      toast.error('Erro ao encerrar acesso')
    } finally {
      setSalvandoId(null)
    }
  }

  const indicadoresUnicos = useMemo(() => {
    const s = new Set<string>()
    inscritos.forEach(r => { if (r.indicadoPor) s.add(r.indicadoPor) })
    return Array.from(s).sort()
  }, [inscritos])

  const filtrados = useMemo(() => inscritos.filter(r => {
    const mBusca = !busca ||
      (r.nome ?? '').toLowerCase().includes(busca.toLowerCase()) ||
      (r.whatsapp ?? '').includes(busca) ||
      (r.email ?? '').toLowerCase().includes(busca.toLowerCase())
    const mEtapa = filtroEtapa === 'todos' || r.etapaAtual === filtroEtapa
    const mIndicado = filtroIndicado === 'todos' || (r.indicadoPor || '') === filtroIndicado
    return mBusca && mEtapa && mIndicado
  }), [inscritos, busca, filtroEtapa, filtroIndicado])

  const totalInscritos = inscritos.length
  const totalEmAndamento = inscritos.filter(r => r.etapaAtual?.startsWith('aula')).length
  const totalConcluiram = inscritos.filter(r => r.etapaAtual === 'certificado_emitido' || r.etapaAtual === 'acesso_criado').length
  const totalAcessoAtivo = inscritos.filter(r => {
    const d = diasRestantesAcesso(r)
    return d !== null && d >= 0 && (r.etapaAtual === 'certificado_emitido' || r.etapaAtual === 'acesso_criado')
  }).length
  const totalAcessoExpirado = inscritos.filter(r => {
    const d = diasRestantesAcesso(r)
    return d !== null && d < 0
  }).length

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
          <Award className="w-6 h-6 text-verde-escuro" />
          Capacitação — Uma Gota de Equilíbrio
        </h2>
        <p className="text-sm text-texto-suave">
          {isAdminProp ? 'Acompanhe todos os participantes do curso introdutório.' : 'Pessoas que se inscreveram pelo seu link de capacitação.'}
        </p>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total de inscritos', valor: totalInscritos, cor: 'text-texto-escuro' },
          { label: 'Em andamento', valor: totalEmAndamento, cor: 'text-blue-600' },
          { label: 'Concluíram o curso', valor: totalConcluiram, cor: 'text-verde-escuro' },
          { label: 'Acesso ativo', valor: totalAcessoAtivo, cor: 'text-[#2D6A4F]' },
          { label: 'Acesso expirado', valor: totalAcessoExpirado, cor: 'text-red-600' },
        ].map(m => (
          <div key={m.label} className="bg-white p-5 rounded-xl shadow-md">
            <div className={`text-3xl font-bold mb-1 ${m.cor}`}>{m.valor}</div>
            <div className="text-xs text-texto-suave">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-texto-suave mb-1">Buscar</label>
            <input
              type="text"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Nome, WhatsApp ou e-mail"
              className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-texto-suave mb-1">Status do curso</label>
            <select
              value={filtroEtapa}
              onChange={e => setFiltroEtapa(e.target.value)}
              className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm"
            >
              <option value="todos">Todos</option>
              <option value="selecao_concluida">Inscrição concluída</option>
              <option value="aula1">Em andamento — aula 1</option>
              <option value="aula2">Em andamento — aula 2</option>
              <option value="aula3">Em andamento — aula 3</option>
              <option value="aula4">Em andamento — aula 4</option>
              <option value="aula5">Em andamento — aula 5</option>
              <option value="aula6">Em andamento — aula 6</option>
              <option value="aula7">Em andamento — aula 7</option>
              <option value="certificado_emitido">Curso concluído</option>
              <option value="acesso_criado">Acesso criado</option>
            </select>
          </div>
          {isAdminProp && (
            <div>
              <label className="block text-xs text-texto-suave mb-1">Indicado por</label>
              <select
                value={filtroIndicado}
                onChange={e => setFiltroIndicado(e.target.value)}
                className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm"
              >
                <option value="todos">Todas as consultoras</option>
                {indicadoresUnicos.map(u => (
                  <option key={u} value={u}>{nomeConsultora(u)} ({u})</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {carregando ? (
          <div className="p-8 text-center text-texto-suave">Carregando...</div>
        ) : filtrados.length === 0 ? (
          <div className="p-8 text-center text-texto-suave">Nenhum registro encontrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bege-fundo">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">WhatsApp</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                  {isAdminProp && <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Indicado por</th>}
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Inscrição</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Conclusão</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Acesso</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bege-medio">
                {filtrados.map(reg => {
                  const etapaCores = corEtapa(reg.etapaAtual)
                  const dias = diasRestantesAcesso(reg)
                  const loading = salvandoId === reg._id
                  return (
                    <tr key={reg._id} className="hover:bg-bege-fundo">
                      <td className="px-4 py-3 text-sm text-texto-escuro font-medium">
                        {reg.nome}
                        <div className="text-xs text-texto-suave">{reg.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`https://wa.me/${(reg.whatsapp ?? '').replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-verde-escuro hover:underline"
                        >
                          {reg.whatsapp}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: etapaCores.bg, color: etapaCores.text }}
                        >
                          {labelEtapa(reg.etapaAtual)}
                        </span>
                      </td>
                      {isAdminProp && (
                        <td className="px-4 py-3 text-sm text-texto-suave">
                          {nomeConsultora(reg.indicadoPor)}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">
                        {reg.dataInscricao ? new Date(reg.dataInscricao).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">
                        {reg.dataCertificado ? new Date(reg.dataCertificado).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {dias === null ? (
                          <span className="text-texto-suave text-xs">—</span>
                        ) : dias < 0 ? (
                          <span className="text-red-600 text-xs font-medium">Expirado</span>
                        ) : (
                          <span className={`text-xs font-medium ${dias <= 3 ? 'text-yellow-700' : 'text-verde-escuro'}`}>
                            {dias} dia(s) restante(s)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            onClick={() => setSelecionado(reg)}
                            className="px-2.5 py-1.5 text-xs bg-[#E6F1FB] text-[#185FA5] hover:bg-blue-100 rounded-lg transition-colors font-medium flex items-center gap-1"
                            title="Ver detalhes"
                          >
                            <Eye className="w-3 h-3" />
                            Detalhes
                          </button>
                          {isAdminProp && (
                            <>
                              <button
                                onClick={() => liberarAcessoPermanente(reg)}
                                disabled={loading}
                                className="px-2.5 py-1.5 text-xs bg-[#D8F3DC] text-[#2D6A4F] hover:bg-[#52B788] hover:text-white rounded-lg transition-colors font-medium disabled:opacity-60"
                                title="Liberar acesso gratuito permanente"
                              >
                                Liberar acesso
                              </button>
                              <button
                                onClick={() => estenderAcesso(reg, 7)}
                                disabled={loading}
                                className="px-2.5 py-1.5 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors font-medium disabled:opacity-60"
                                title="Estender acesso em 7 dias"
                              >
                                +7 dias
                              </button>
                              <button
                                onClick={() => encerrarAcesso(reg)}
                                disabled={loading}
                                className="px-2.5 py-1.5 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors font-medium disabled:opacity-60"
                                title="Encerrar acesso"
                              >
                                Encerrar
                              </button>
                            </>
                          )}
                          <a
                            href={`https://wa.me/${(reg.whatsapp ?? '').replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 text-xs bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors font-medium"
                            title="Falar no WhatsApp"
                          >
                            WhatsApp
                          </a>
                          {isAdminProp && (
                            <button
                              onClick={() => setConfirmarDelete(reg)}
                              disabled={loading}
                              className="px-2.5 py-1.5 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors font-medium disabled:opacity-60 flex items-center gap-1"
                              title="Deletar da seleção"
                            >
                              <Trash2 className="w-3 h-3" />
                              Deletar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Modal de detalhes da inscrita */}
      {selecionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#2D6A4F] text-white px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
              <div>
                <h2 className="text-xl font-cormorant font-semibold flex items-center gap-2">
                  <Award className="w-5 h-5" /> {selecionado.nome}
                </h2>
                <p className="text-[#A8DFC4] text-xs mt-0.5">
                  Inscrita em {selecionado.dataInscricao ? new Date(selecionado.dataInscricao).toLocaleDateString('pt-BR') : '—'}
                </p>
              </div>
              <button onClick={() => setSelecionado(null)} className="text-white hover:text-gray-200">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Dados básicos */}
              <section>
                <h3 className="text-base font-semibold text-[#2D6A4F] mb-3 pb-1 border-b border-bege-medio">Dados pessoais</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Nome completo', valor: selecionado.nome },
                    { label: 'E-mail', valor: selecionado.email },
                    { label: 'WhatsApp', valor: selecionado.whatsapp },
                    { label: 'País', valor: selecionado.pais || 'Brasil' },
                    { label: 'Data de inscrição', valor: selecionado.dataInscricao ? new Date(selecionado.dataInscricao).toLocaleString('pt-BR') : '—' },
                    { label: 'Indicado por', valor: nomeConsultora(selecionado.indicadoPor) },
                  ].map(({ label, valor }) => (
                    <div key={label}>
                      <div className="text-xs text-texto-suave mb-0.5 font-medium">{label}</div>
                      <div className="text-sm text-texto-escuro">{valor || '—'}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Respostas do formulário */}
              <section>
                <h3 className="text-base font-semibold text-[#2D6A4F] mb-3 pb-1 border-b border-bege-medio">Respostas do formulário de seleção</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Status profissional atual', valor: selecionado.status },
                    { label: 'Gosta de compartilhar / transformar', valor: selecionado.perfilResposta },
                    { label: 'Relação com óleos essenciais', valor: selecionado.relacaoOleos },
                    { label: 'Relação com dōTERRA', valor: selecionado.relacaoDoterra },
                  ].map(({ label, valor }) => valor ? (
                    <div key={label} className="bg-bege-fundo rounded-xl px-4 py-3">
                      <div className="text-xs text-[#B08968] font-medium mb-1">{label}</div>
                      <div className="text-sm text-texto-escuro">{valor}</div>
                    </div>
                  ) : null)}
                </div>
              </section>

              {/* Status do curso */}
              <section>
                <h3 className="text-base font-semibold text-[#2D6A4F] mb-3 pb-1 border-b border-bege-medio">Status no curso</h3>
                <div className="flex flex-wrap gap-4">
                  <div>
                    <div className="text-xs text-texto-suave mb-1">Etapa atual</div>
                    <span
                      className="px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: corEtapa(selecionado.etapaAtual).bg, color: corEtapa(selecionado.etapaAtual).text }}
                    >
                      {labelEtapa(selecionado.etapaAtual)}
                    </span>
                  </div>
                  {selecionado.aulasCompletas?.length > 0 && (
                    <div>
                      <div className="text-xs text-texto-suave mb-1">Aulas concluídas</div>
                      <div className="text-sm text-texto-escuro font-medium">{selecionado.aulasCompletas.length} / 7</div>
                    </div>
                  )}
                  {selecionado.dataCertificado && (
                    <div>
                      <div className="text-xs text-texto-suave mb-1">Data de conclusão</div>
                      <div className="text-sm text-texto-escuro">{new Date(selecionado.dataCertificado).toLocaleDateString('pt-BR')}</div>
                    </div>
                  )}
                  {selecionado.dataFimAcesso && (
                    <div>
                      <div className="text-xs text-texto-suave mb-1">Acesso ao curso até</div>
                      <div className="text-sm text-texto-escuro">{new Date(selecionado.dataFimAcesso).toLocaleDateString('pt-BR')}</div>
                    </div>
                  )}
                </div>
              </section>

              {/* Ações */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-bege-medio">
                <a
                  href={`https://wa.me/${(selecionado.whatsapp ?? '').replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#1ea34e] transition-all"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                {isAdminProp && (
                  <>
                    <button
                      onClick={() => { liberarAcessoPermanente(selecionado); setSelecionado(null) }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-[#D8F3DC] text-[#2D6A4F] hover:bg-[#52B788] hover:text-white rounded-xl text-sm font-medium transition-all"
                    >
                      Liberar acesso
                    </button>
                    <button
                      onClick={() => { setConfirmarDelete(selecionado); setSelecionado(null) }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-xl text-sm font-medium transition-all"
                    >
                      <Trash2 className="w-4 h-4" /> Deletar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão de inscrito da capacitação */}
      {confirmarDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">
                  Deletar {confirmarDelete.nome} da seleção?
                </h3>
              </div>
            </div>
            <p className="text-texto-suave text-sm mb-6 leading-relaxed">
              Tem certeza que deseja deletar <strong>{confirmarDelete.nome}</strong> da seleção?
              Essa ação não pode ser desfeita. O WhatsApp ficará liberado para uma nova inscrição futura.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmarDelete(null)}
                disabled={deletando}
                className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={() => deletarInscrito(confirmarDelete)}
                disabled={deletando}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" />
                {deletando ? 'Deletando...' : 'Sim, deletar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Admin2() {
  // Autenticacao
  const [autenticado, setAutenticado] = useState(false)
  const [perfilLogado, setPerfilLogado] = useState<PerfilLogado | null>(null)
  const [loginUsuario, setLoginUsuario] = useState('')
  const [loginSenha, setLoginSenha] = useState('')

  // Exibição da página de perfil (unificada com a aba Meu Perfil)
  const [mostrandoPerfil, setMostrandoPerfil] = useState(false)

  // Modal "Esqueceu senha?"
  const [modalEsqueciSenha, setModalEsqueciSenha] = useState(false)
  const [emailRecuperacao, setEmailRecuperacao] = useState('')
  const [enviandoRecuperacao, setEnviandoRecuperacao] = useState(false)
  const [recuperacaoEnviada, setRecuperacaoEnviada] = useState(false)

  // Aba ativa
  const [abaAtiva, setAbaAtiva] = useState<AbaAdmin>('leads')

  // Badge de notificação de leads novos
  // chave no localStorage: admin2_ultimo_acesso_{usuario}
  const [ultimoAcessoTs, setUltimoAcessoTs] = useState<string | null>(null)
  const [leadsNovosVistos, setLeadsNovosVistos] = useState<Set<string>>(new Set())

  // Leads
  const [leads, setLeads] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [filtroStatus, setFiltroStatus] = useState<string>('todos')
  const [filtroConsultora, setFiltroConsultora] = useState<string>('todos')
  const [filtroEmpresa, setFiltroEmpresa] = useState<string>('todos')
  const [busca, setBusca] = useState('')
  const [leadSelecionado, setLeadSelecionado] = useState<any>(null)
  const [modalAberto, setModalAberto] = useState(false)

  // Leads Kids
  const [leadsKids, setLeadsKids] = useState<any[]>([])
  const [loadingKids, setLoadingKids] = useState(false)
  const [filtroStatusKids, setFiltroStatusKids] = useState<string>('todos')
  const [filtroFaixaKids, setFiltroFaixaKids] = useState<string>('todos')
  const [filtroConsultoraKids, setFiltroConsultoraKids] = useState<string>('todos')
  const [buscaKids, setBuscaKids] = useState('')
  const [leadKidsSelecionado, setLeadKidsSelecionado] = useState<any>(null)

  // Consultoras
  const [consultoras, setConsultoras] = useState<Consultora[]>([])
  const [loadingConsultoras, setLoadingConsultoras] = useState(false)
  const [novaConsultoraNome, setNovaConsultoraNome] = useState('')
  const [novaConsultoraUsuario, setNovaConsultoraUsuario] = useState('')
  const [novaConsultoraEmail, setNovaConsultoraEmail] = useState('')
  const [precisaTrocarSenha, setPrecisaTrocarSenha] = useState(false)

  // Gestao de Acessos (cadastro manual)
  const [acessoNome, setAcessoNome] = useState('')
  const [acessoEmail, setAcessoEmail] = useState('')
  const [acessoWhatsapp, setAcessoWhatsapp] = useState('')
  const [acessoUsuario, setAcessoUsuario] = useState('')
  const [acessoSenha, setAcessoSenha] = useState('')
  const [acessoTipo, setAcessoTipo] = useState<TipoAcesso>('gratuito')
  const [acessoDataExpiracao, setAcessoDataExpiracao] = useState('')
  const [acessoStatus, setAcessoStatus] = useState<StatusAssinatura>('gratuito')
  const [acessoPlano, setAcessoPlano] = useState<PlanoAssinatura>('essencial')
  const [editandoConsultora, setEditandoConsultora] = useState<Consultora | null>(null)

  // Modal de confirmacao de exclusao de consultora
  const [confirmarExclusao, setConfirmarExclusao] = useState<Consultora | null>(null)

  // Empresas (para métricas por consultora no painel admin)
  const [empresasList, setEmpresasList] = useState<any[]>([])

  // Detalhe de consultora (visão restrita da Mônica)
  const [consultoraDetalhe, setConsultoraDetalhe] = useState<Consultora | null>(null)

  // Distribuidoras
  const [distribuidoras, setDistribuidoras] = useState<Distribuidora[]>([])
  const [loadingDistribuidoras, setLoadingDistribuidoras] = useState(false)
  const [novaDistNome, setNovaDistNome] = useState('')
  const [novaDistSlug, setNovaDistSlug] = useState('')
  const [novaDistEmail, setNovaDistEmail] = useState('')
  const [novaDistWhats, setNovaDistWhats] = useState('')
  const [novaDistSenha, setNovaDistSenha] = useState('')
  const [novaDistTotal, setNovaDistTotal] = useState('100')

  // ══════════════════════════════════════════
  // AUTENTICACAO
  // ══════════════════════════════════════════

  useEffect(() => {
    const sessao = localStorage.getItem('admin2_sessao')
    if (sessao) {
      try {
        const parsed = JSON.parse(sessao) as PerfilLogado
        setPerfilLogado(parsed)
        setAutenticado(true)
        // Restaurar timestamp de último acesso para o badge
        if (parsed.tipo === 'consultora') {
          const ts = localStorage.getItem(`admin2_ultimo_acesso_${parsed.usuario}`)
          setUltimoAcessoTs(ts)
          // Atualizar timestamp agora (reload = novo acesso)
          const novoTs = new Date().toISOString()
          localStorage.setItem(`admin2_ultimo_acesso_${parsed.usuario}`, novoTs)
          setUltimoAcessoTs(novoTs)
        }
      } catch {
        localStorage.removeItem('admin2_sessao')
      }
    }
  }, [])

  // Se consultora logada expirar, redireciona para /renovar
  useEffect(() => {
    if (!autenticado || !perfilLogado || perfilLogado.tipo !== 'consultora') return
    ;(async () => {
      try {
        const resultado = await lumi.entities.consultoras.list({
          filter: { usuario: perfilLogado.usuario },
          limit: 1,
        })
        const atual = (resultado.list || []).find((c: any) => c.usuario === perfilLogado.usuario)
        if (atual && !acessoLiberado(atual)) {
          localStorage.removeItem('admin2_sessao')
          window.location.href = '/renovar'
          return
        }
        if (atual) {
          setPrecisaTrocarSenha(atual.precisaTrocarSenha === true || atual.primeiroAcesso === true)
        }
      } catch (e) {
        console.error('Erro ao verificar assinatura:', e)
      }
    })()
  }, [autenticado, perfilLogado])

  // Abrir a aba completa de perfil (com foto) pela rota /perfil
  useEffect(() => {
    if (autenticado && perfilLogado?.tipo === 'consultora' && window.location.pathname === '/perfil') {
      setAbaAtiva('perfil')
      setMostrandoPerfil(false)
    }
  }, [autenticado, perfilLogado])

  // Fluxo "Esqueceu sua senha?"
  const solicitarRedefinicao = async (e: React.FormEvent) => {
    e.preventDefault()
    const emailTrim = emailRecuperacao.trim().toLowerCase()
    if (!emailTrim) {
      toast.error('Informe seu e-mail')
      return
    }

    setEnviandoRecuperacao(true)
    try {
      // Buscar consultora filtrando por e-mail direto na consulta
      const resultado = await lumi.entities.consultoras.list({
        filter: { email: emailTrim },
        limit: 10,
      })
      let consultora = (resultado.list || []).find(
        (c: any) => (c.email || '').toLowerCase().trim() === emailTrim
      ) as Consultora | undefined

      // Fallback: e-mail gravado com maiúsculas/espaços ou sem correspondência exata no filtro
      if (!consultora) {
        const todos = await listarTodos(lumi.entities.consultoras)
        consultora = todos.find(
          (c: any) => (c.email || '').toLowerCase().trim() === emailTrim
        ) as Consultora | undefined
      }

      if (!consultora) {
        toast.error('E-mail não encontrado. Verifique o endereço informado ou cadastre-se gratuitamente.')
        setEnviandoRecuperacao(false)
        return
      }

      // Gerar token simples com expiração de 24h (base64 de JSON)
      const payload = {
        usuario: consultora.usuario,
        exp: Date.now() + 24 * 60 * 60 * 1000,
      }
      const token = btoa(JSON.stringify(payload))
      const linkRedefinicao = `${window.location.origin}/redefinir-senha?token=${encodeURIComponent(token)}`

      // Salvar token ativo na consultora (invalida tokens anteriores)
      await lumi.entities.consultoras.update(consultora._id, {
        resetTokenAtivo: token,
        updatedAt: new Date().toISOString(),
      })

      // Enviar e-mail
      const res = await fetch(EMAIL_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'redefinir_senha',
          nome: consultora.nome,
          email: consultora.email,
          linkRedefinicao,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        console.error('Erro ao enviar e-mail de redefinição:', data)
        toast.error('Erro ao enviar e-mail. Tente novamente em instantes.')
        return
      }

      setRecuperacaoEnviada(true)
      toast.success('E-mail enviado! Verifique sua caixa de entrada.')
    } catch (err) {
      console.error('Erro ao solicitar redefinição:', err)
      toast.error('Erro ao processar solicitação. Tente novamente.')
    } finally {
      setEnviandoRecuperacao(false)
    }
  }

  const fecharModalEsqueciSenha = () => {
    setModalEsqueciSenha(false)
    setEmailRecuperacao('')
    setRecuperacaoEnviada(false)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    const usuario = loginUsuario.trim().toLowerCase()
    const senha = loginSenha.trim()

    if (!usuario && !senha) {
      toast.error('Preencha os campos de login')
      return
    }

    // Admin
    if ((!usuario || usuario === 'admin') && senha === SENHA_ADMIN) {
      const perfil: PerfilLogado = { tipo: 'admin' }
      setPerfilLogado(perfil)
      setAutenticado(true)
      localStorage.setItem('admin2_sessao', JSON.stringify(perfil))
      toast.success('Bem-vindo, administrador!')
      return
    }

    // Consultora
    if (usuario) {
      try {
        const resultado = await lumi.entities.consultoras.list({
          filter: { usuario: usuario },
          limit: 1,
        })
        const consultoraEncontrada = (resultado.list || []).find(
          (c: any) => c.usuario === usuario
        ) as Consultora | undefined

        if (consultoraEncontrada) {
          if (!consultoraEncontrada.ativo) {
            toast.error('Seu acesso foi desativado. Entre em contato com a administração.')
            return
          }
          if (consultoraEncontrada.senha === senha) {
            // Verificar assinatura
            if (!acessoLiberado(consultoraEncontrada)) {
              toast.error('Seu acesso expirou. Redirecionando...')
              setTimeout(() => { window.location.href = '/renovar' }, 1000)
              return
            }
            const perfil: PerfilLogado = {
              tipo: 'consultora',
              usuario: consultoraEncontrada.usuario,
              nome: consultoraEncontrada.nome,
              _id: consultoraEncontrada._id,
            }
            setPerfilLogado(perfil)
            setAutenticado(true)
            localStorage.setItem('admin2_sessao', JSON.stringify(perfil))
            // Registrar timestamp de acesso para badge de notificação
            const tsLogin = new Date().toISOString()
            localStorage.setItem(`admin2_ultimo_acesso_${consultoraEncontrada.usuario}`, tsLogin)
            setUltimoAcessoTs(tsLogin)
            const deveTrocarSenha = consultoraEncontrada.precisaTrocarSenha === true || consultoraEncontrada.primeiroAcesso === true
            setPrecisaTrocarSenha(deveTrocarSenha)
            toast.success(deveTrocarSenha ? 'Crie sua senha pessoal para continuar' : `Bem-vindo, ${consultoraEncontrada.nome}!`)
            return
          }
        }
      } catch (error) {
        console.error('Erro ao verificar consultora:', error)
      }
    }

    toast.error('Usuario ou senha incorretos')
  }

  const handleLogout = () => {
    setAutenticado(false)
    setPerfilLogado(null)
    setLoginUsuario('')
    setLoginSenha('')
    setLeads([])
    setConsultoras([])
    setDistribuidoras([])
    setAbaAtiva('leads')
    setPrecisaTrocarSenha(false)
    localStorage.removeItem('admin2_sessao')
    toast.success('Logout realizado!')
  }

  const isAdmin = perfilLogado?.tipo === 'admin'
  const consultoraLogada = perfilLogado?.tipo === 'consultora' ? perfilLogado.usuario : null

  // Consultora logada (para exibir nome no bloco Como Divulgar)
  const consultoraLogadaObj = useMemo(() => {
    if (!consultoraLogada) return null
    return consultoras.find(c => c.usuario === consultoraLogada) || null
  }, [consultoras, consultoraLogada])

  // ══════════════════════════════════════════
  // CARREGAR DADOS
  // ══════════════════════════════════════════

  useEffect(() => {
    if (!autenticado) return
    carregarLeads()
    carregarLeadsKids()
    carregarConsultoras() // sempre — usado para exibir nome no bloco Como Divulgar
    if (isAdmin) {
      carregarDistribuidoras()
      carregarEmpresasList()
    }
  }, [autenticado, isAdmin])

  const carregarEmpresasList = async () => {
    try {
      const resultado = await lumi.entities.empresas.list({ sort: { createdAt: -1 }, limit: 500 })
      setEmpresasList(resultado?.list ?? [])
    } catch (error) {
      console.error('Erro ao carregar empresas:', error)
    }
  }

  // Carrega TODOS os registros paginando em blocos de 500 (sem esconder dados)
  const listarTodos = async (
    entidade: any,
    options: Record<string, any> = {}
  ): Promise<any[]> => {
    const TAMANHO_PAGINA = 500
    let acumulado: any[] = []
    let skip = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const resposta = await entidade.list({ ...options, limit: TAMANHO_PAGINA, skip })
      const lote = resposta?.list ?? []
      acumulado = acumulado.concat(lote)
      if (lote.length < TAMANHO_PAGINA) break
      skip += TAMANHO_PAGINA
      if (skip >= 10000) break
    }
    return acumulado
  }

  const carregarLeads = async () => {
    setLoading(true)
    try {
      const todos = await listarTodos(lumi.entities.leads2, { sort: { data_hora: -1 } })
      const leadsDecodificados = todos.map(lead => decodeUnicodeString(lead))
      setLeads(leadsDecodificados)
    } catch (error) {
      console.error('Erro ao carregar leads:', error)
      toast.error('Erro ao carregar leads')
    } finally {
      setLoading(false)
    }
  }

  const carregarLeadsKids = async () => {
    setLoadingKids(true)
    try {
      const resultado = await lumi.entities.leads_kids.list({ sort: { data_hora: -1 }, limit: 500 })
      setLeadsKids(resultado?.list ?? [])
    } catch (error) {
      console.error('Erro ao carregar leads kids:', error)
    } finally {
      setLoadingKids(false)
    }
  }

  const carregarConsultoras = async () => {
    setLoadingConsultoras(true)
    try {
      const todos = await listarTodos(lumi.entities.consultoras, { sort: { createdAt: -1 } })
      const consultorasDecodificadas = todos.map(c => decodeUnicodeString(c))
      setConsultoras(consultorasDecodificadas)
    } catch (error) {
      console.error('Erro ao carregar consultores:', error)
      toast.error('Erro ao carregar consultores')
    } finally {
      setLoadingConsultoras(false)
    }
  }

  const carregarDistribuidoras = async () => {
    setLoadingDistribuidoras(true)
    try {
      const todos = await listarTodos(lumi.entities.distribuidoras, { sort: { createdAt: -1 } })
      const dists = todos.map((d: any) => decodeUnicodeString(d))
      setDistribuidoras(dists)
    } catch (error) {
      console.error('Erro ao carregar distribuidoras:', error)
    } finally {
      setLoadingDistribuidoras(false)
    }
  }

  // ══════════════════════════════════════════
  // LEADS — FILTROS E METRICAS
  // ══════════════════════════════════════════

  const leadsVisiveis = useMemo(() => {
    if (isAdmin) return leads
    if (consultoraLogada) return leads.filter(l => l.consultora === consultoraLogada)
    return []
  }, [leads, isAdmin, consultoraLogada])

  const leadsFiltrados = useMemo(() => {
    return leadsVisiveis.filter(lead => {
      const matchStatus = filtroStatus === 'todos' || lead.status === filtroStatus
      const matchConsultora = filtroConsultora === 'todos' || (lead.consultora || 'geral') === filtroConsultora
      const matchEmpresa = filtroEmpresa === 'todos' || (lead.empresa || '') === filtroEmpresa
      const matchBusca =
        !busca ||
        lead.nome?.toLowerCase().includes(busca.toLowerCase()) ||
        lead.whatsapp?.includes(busca)
      return matchStatus && matchConsultora && matchEmpresa && matchBusca
    })
  }, [leadsVisiveis, filtroStatus, filtroConsultora, filtroEmpresa, busca])

  const stats = useMemo(() => ({
    total: leadsVisiveis.length,
    novo: leadsVisiveis.filter(l => l.status === 'Novo lead').length,
    contato: leadsVisiveis.filter(l => l.status === 'Em contato').length,
    agendado: leadsVisiveis.filter(l => l.status === 'Agendado').length,
    convertido: leadsVisiveis.filter(l => l.status === 'Convertido').length,
    naoRespondeu: leadsVisiveis.filter(l => l.status === 'Não respondeu').length,
    naoQualificado: leadsVisiveis.filter(l => l.status === 'Não qualificado').length,
  }), [leadsVisiveis])

  // Leads considerados "novos" (chegaram após o último acesso da consultora)
  const leadsNovosIds = useMemo(() => {
    if (!ultimoAcessoTs || !consultoraLogada) return new Set<string>()
    const set = new Set<string>()
    leadsVisiveis.forEach(l => {
      if (l.data_hora && l.data_hora > ultimoAcessoTs) {
        set.add(l._id)
      }
    })
    return set
  }, [leadsVisiveis, ultimoAcessoTs, consultoraLogada])

  const quantidadeLeadsNovos = useMemo(() => {
    let count = 0
    leadsNovosIds.forEach(id => {
      if (!leadsNovosVistos.has(id)) count++
    })
    return count
  }, [leadsNovosIds, leadsNovosVistos])

  const irParaLeadsKids = () => setAbaAtiva('leads_kids')

  // Marcar todos os leads novos como vistos ao abrir a aba de leads
  const irParaLeads = () => {
    setAbaAtiva('leads')
    setLeadsNovosVistos(prev => {
      const next = new Set(prev)
      leadsNovosIds.forEach(id => next.add(id))
      return next
    })
  }

  const consultorasUnicas = useMemo(() => {
    const set = new Set<string>()
    leads.forEach(l => set.add(l.consultora || 'geral'))
    return Array.from(set).sort()
  }, [leads])

  const empresasUnicas = useMemo(() => {
    const set = new Set<string>()
    leadsVisiveis.forEach(l => { if (l.empresa) set.add(l.empresa) })
    return Array.from(set).sort()
  }, [leadsVisiveis])

  const empresasNomes = useMemo(() => {
    const map: Record<string, string> = {}
    leadsVisiveis.forEach(l => { if (l.empresa && l.empresaNome) map[l.empresa] = l.empresaNome })
    return map
  }, [leadsVisiveis])

  const leadsCountPorConsultora = useMemo(() => {
    const map: Record<string, number> = {}
    leads.forEach(l => {
      const c = l.consultora || 'geral'
      map[c] = (map[c] || 0) + 1
    })
    return map
  }, [leads])

  // ══════════════════════════════════════════
  // VISÃO AGREGADA PARA A ADMINISTRADORA
  // Apenas contagens por consultora — SEM nomes, WhatsApp, e-mail
  // de clientes e SEM respostas individuais dos diagnósticos.
  // ══════════════════════════════════════════
  const breakdownAdultoPorConsultora = useMemo(() => {
    const map: Record<string, { usuario: string; nome: string; total: number; novo: number; contato: number; agendado: number; convertido: number; naoRespondeu: number; naoQualificado: number }> = {}
    leads.forEach(l => {
      const usuario = l.consultora || 'geral'
      if (!map[usuario]) {
        const c = consultoras.find(x => x.usuario === usuario)
        map[usuario] = { usuario, nome: c?.nome || (usuario === 'geral' ? 'Sem consultora vinculada' : usuario), total: 0, novo: 0, contato: 0, agendado: 0, convertido: 0, naoRespondeu: 0, naoQualificado: 0 }
      }
      map[usuario].total++
      if (l.status === 'Novo lead') map[usuario].novo++
      else if (l.status === 'Em contato') map[usuario].contato++
      else if (l.status === 'Agendado') map[usuario].agendado++
      else if (l.status === 'Convertido') map[usuario].convertido++
      else if (l.status === 'Não respondeu') map[usuario].naoRespondeu++
      else if (l.status === 'Não qualificado') map[usuario].naoQualificado++
    })
    return Object.values(map).sort((a, b) => b.total - a.total)
  }, [leads, consultoras])

  const breakdownKidsPorConsultora = useMemo(() => {
    const map: Record<string, { usuario: string; nome: string; total: number; novo: number; contato: number; convertido: number }> = {}
    leadsKids.forEach(l => {
      const usuario = l.consultora || 'geral'
      if (!map[usuario]) {
        const c = consultoras.find(x => x.usuario === usuario)
        map[usuario] = { usuario, nome: c?.nome || (usuario === 'geral' ? 'Sem consultora vinculada' : usuario), total: 0, novo: 0, contato: 0, convertido: 0 }
      }
      map[usuario].total++
      if (l.status === 'Novo lead') map[usuario].novo++
      else if (l.status === 'Em contato') map[usuario].contato++
      else if (l.status === 'Convertido') map[usuario].convertido++
    })
    return Object.values(map).sort((a, b) => b.total - a.total)
  }, [leadsKids, consultoras])

  // ══════════════════════════════════════════
  // LEADS — LABELS E ACOES
  // ══════════════════════════════════════════

  const labelsPerguntas: Record<string, string> = {
    'sono': 'Sono (Dificuldade para dormir)',
    'disposicao': 'Disposição (Cansaço e falta de energia)',
    'foco': 'Foco (Distração ou dispersão mental)',
    'memoria': 'Memória (Esquecimento ou falta de clareza)',
    'ansiedade': 'Ansiedade (Inquietação interior ou tensão no corpo)',
    'peso': 'Peso (Oscilação de peso ou alimentação emocional)',
    'dores': 'Dores (Dores de cabeça, no corpo ou tensão muscular)',
    'medicamentos': 'Medicamentos (Uso recorrente de medicamentos)',
    'inflamacoes': 'Imunidade (Baixa imunidade ou quadros inflamatórios)',
    'intestino': 'Intestino (Desconfortos digestivos)',
    'vergonha': 'Vergonha (Medo do julgamento)',
    'culpa': 'Culpa (Excesso de responsabilidade)',
    'apatia': 'Apatia (Indiferença e perda de interesse)',
    'tristeza': 'Tristeza (Apego ao passado e falta de esperança)',
    'medo': 'Medo (Resistência a mudanças e ao desconhecido)',
    'escassez': 'Escassez (Sentimento de falta e insatisfação)',
    'raiva': 'Raiva (Irritabilidade fácil)',
    'orgulho': 'Orgulho (Dificuldade de pedir ajuda)',
    'estagnacao': 'Estagnação (Ciclos que se repetem)',
    'desequilibrio': 'Desequilíbrio (Oscilação emocional)',
  }

  // Chaves das perguntas 1 a 10 (Saúde Física) e 11 a 20 (Saúde Emocional),
  // usadas para separar a exibição dos sintomas em dois grupos distintos
  const CHAVES_SINTOMAS_FISICOS = ['sono', 'disposicao', 'foco', 'memoria', 'ansiedade', 'peso', 'dores', 'medicamentos', 'inflamacoes', 'intestino']
  const CHAVES_SENTIMENTOS_EMOCIONAIS = ['vergonha', 'culpa', 'apatia', 'tristeza', 'medo', 'escassez', 'raiva', 'orgulho', 'estagnacao', 'desequilibrio']

  const obterCoresStatus = (status: string) => {
    const cores: Record<string, { bg: string; text: string }> = {
      'Novo lead': { bg: '#E6F1FB', text: '#185FA5' },
      'Em contato': { bg: '#FAEEDA', text: '#854F0B' },
      'Agendado': { bg: '#F3E8FF', text: '#6B21A8' },
      'Convertido': { bg: '#D8F3DC', text: '#2D6A4F' },
      'Não respondeu': { bg: '#FCEBEB', text: '#A32D2D' },
      'Não qualificado': { bg: '#F3F4F6', text: '#4B5563' },
    }
    return cores[status] || { bg: '#F3F4F6', text: '#6B7280' }
  }

  const atualizarStatus = async (leadId: string, novoStatus: string) => {
    try {
      await lumi.entities.leads2.update(leadId, {
        status: novoStatus,
        updatedAt: new Date().toISOString(),
      })
      toast.success('Status atualizado!')
      carregarLeads()
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      toast.error('Erro ao atualizar status')
    }
  }

  const excluirLead = async (leadId: string) => {
    if (!confirm('Tem certeza que deseja excluir este lead?')) return
    try {
      await lumi.entities.leads2.delete(leadId)
      toast.success('Lead excluído!')
      carregarLeads()
      setModalAberto(false)
    } catch (error) {
      console.error('Erro ao excluir lead:', error)
      toast.error('Erro ao excluir lead')
    }
  }

  const formatarNumeroWhatsApp = (whatsapp: string): string => {
    return (whatsapp || '').replace(/\D/g, '')
  }

  const exibirWhatsAppFormatado = (whatsapp: string, pais: string): string => {
    const digitos = (whatsapp || '').replace(/\D/g, '')
    if (pais === 'Portugal') {
      if (digitos.startsWith('351')) {
        const numero = digitos.slice(3)
        return `+351 ${numero}`
      }
      return `+351 ${digitos}`
    } else {
      if (digitos.startsWith('55')) {
        const numero = digitos.slice(2)
        if (numero.length === 11) {
          return `+55 (${numero.slice(0, 2)}) ${numero.slice(2, 3)} ${numero.slice(3, 7)}-${numero.slice(7)}`
        }
        return `+55 ${numero}`
      }
      if (digitos.length === 11) {
        return `+55 (${digitos.slice(0, 2)}) ${digitos.slice(2, 3)} ${digitos.slice(3, 7)}-${digitos.slice(7)}`
      }
      return `+55 ${digitos}`
    }
  }

  const montarResumo = (lead: any): string => {
    let texto = `*Nome:* ${lead.nome || ''}\n*WhatsApp:* ${lead.whatsapp || ''}\n\n*RESPOSTAS DO DIAGNÓSTICO*\n`
    texto += `\n*Condições de saúde:*\n`
    if (lead.condicoes_saude?.length > 0) {
      lead.condicoes_saude.forEach((c: string) => { texto += `- ${c}\n` })
    } else {
      texto += `- Nenhuma\n`
    }
    texto += `\n*Sintomas respondidos como SIM:*\n`
    if (lead.respostas && Object.keys(lead.respostas).length > 0) {
      const sintomas = Object.entries(lead.respostas)
        .filter(([_, valor]) => valor)
        .map(([key]) => labelsPerguntas[key] || key)
      if (sintomas.length > 0) {
        sintomas.forEach(s => { texto += `- ${s}\n` })
      } else {
        texto += `- Nenhum\n`
      }
    } else {
      texto += `- Nenhum\n`
    }
    texto += `\n*Óleos essenciais indicados:*\n`
    if (lead.oleos_indicados?.length > 0) {
      lead.oleos_indicados.forEach((o: string) => { texto += `- ${nomeExibicaoOleo(o)}\n` })
    } else {
      texto += `- Nenhum\n`
    }
    texto += `\n*Experiência com óleos essenciais:* ${lead.experiencia_oleos || 'Não informado'}\n`
    texto += `\n*Prontidão para começar:* ${lead.prontidao || 'Não informado'}\n`
    texto += `\n*Maior obstáculo:* ${lead.obstaculo || 'Não informado'}\n`
    texto += `\n*Interesses:*\n`
    if (lead.interesses?.length > 0) {
      lead.interesses.forEach((i: string) => { texto += `- ${i}\n` })
    } else {
      texto += `- Não informado\n`
    }
    const prioridades = lead.prioridades || []
    if (prioridades.length > 0) {
      texto += `\n*Prioridades selecionadas:*\n`
      prioridades.forEach((p: string) => { texto += `- ${p}\n` })
    } else {
      texto += `\n*O que mais quer resolver na saúde:*\n${lead.objetivo_saude || 'Não informado'}\n`
    }
    return texto
  }

  const copiarResumo = async (lead: any) => {
    try {
      await copiarTexto(montarResumo(lead))
      toast.success('Resumo copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  const abrirWhatsAppResumo = (lead: any) => {
    const mensagemFormatada = montarResumo(lead)
    const numero = formatarNumeroWhatsApp(lead.whatsapp)
    if (!numero) {
      toast.error('WhatsApp do cliente não disponível')
      return
    }
    const url = `https://wa.me/${numero}`
    window.open(url, '_blank')
  }

  const exportarCSV = () => {
    const headers = ['Data/Hora', 'Nome', 'WhatsApp', 'E-mail', 'Consultora', 'Status', 'Óleos Indicados', 'Clicou no WhatsApp', 'Compartilhou']
    const rows = leadsFiltrados.map(lead => [
      new Date(lead.data_hora).toLocaleString('pt-BR'),
      lead.nome,
      lead.whatsapp,
      lead.email,
      lead.consultora || 'geral',
      lead.status,
      (lead.oleos_indicados || []).join('; '),
      lead.clicou_whatsapp || 'Não',
      lead.compartilhou || 'Não',
    ])
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map(e => e.map(v => `"${v}"`).join(',')).join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', `leads2_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // ══════════════════════════════════════════
  // CONSULTORAS — ACOES (aba basica)
  // ══════════════════════════════════════════

  const cadastrarConsultora = async (e: React.FormEvent) => {
    e.preventDefault()
    const nome = novaConsultoraNome.trim()
    const usuario = novaConsultoraUsuario.trim().toLowerCase().replace(/\s+/g, '')
    const email = novaConsultoraEmail.trim().toLowerCase()
    const senha = gerarSenhaProvisoria()

    if (!nome || !usuario || !email) {
      toast.error('Preencha nome, usuário e e-mail')
      return
    }
    if (consultoras.some(c => c.usuario === usuario)) {
      toast.error('Já existe um(a) consultor(a) com esse usuário')
      return
    }

    try {
      const now = new Date().toISOString()
      await lumi.entities.consultoras.create({
        nome,
        usuario,
        email,
        whatsapp: '',
        senha,
        ativo: true,
        tipoAcesso: 'gratuito',
        statusAssinatura: 'gratuito',
        dataExpiracao: '',
        precisaTrocarSenha: true,
        primeiroAcesso: true,
        migradoLegado: false,
        enviouAvisoVencimento3Dias: false,
        exibirOleosResultado: false,
        exibirOleosResultadoKids: false,
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      })
      try {
        await fetch(EMAIL_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'boas_vindas_equipe',
            nome,
            email,
            usuario,
            senha,
          }),
        })
        toast.success(`Consultor(a) "${nome}" cadastrado(a)! Senha provisória enviada por e-mail.`)
      } catch (emailErr) {
        console.error('Erro ao enviar e-mail de boas-vindas:', emailErr)
        toast.error('Cadastro feito, mas o e-mail com a senha provisória não foi enviado.')
      }
      setNovaConsultoraNome('')
      setNovaConsultoraUsuario('')
      setNovaConsultoraEmail('')
      carregarConsultoras()
    } catch (error) {
      console.error('Erro ao cadastrar consultor(a):', error)
      toast.error('Erro ao cadastrar consultor(a)')
    }
  }

  const toggleAtivoConsultora = async (consultora: Consultora) => {
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        ativo: !consultora.ativo,
        updatedAt: new Date().toISOString(),
      })
      toast.success(consultora.ativo ? 'Consultor(a) desativado(a)' : 'Consultor(a) ativado(a)')
      carregarConsultoras()
    } catch (error) {
      console.error('Erro ao alterar status do(a) consultor(a):', error)
      toast.error('Erro ao alterar status')
    }
  }

  const excluirConsultora = async (id: string) => {
    try {
      await lumi.entities.consultoras.delete(id)
      toast.success('Consultor(a) excluído(a)!')
      setConfirmarExclusao(null)
      carregarConsultoras()
    } catch (error) {
      console.error('Erro ao excluir consultor(a):', error)
      toast.error('Erro ao excluir consultor(a)')
    }
  }

  const desativarReativarConsultora = async (consultora: Consultora) => {
    const novoStatus: StatusAssinatura = consultora.statusAssinatura === 'inativo' ? 'ativo' : 'inativo'
    const novoAtivo = novoStatus !== 'inativo'
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        statusAssinatura: novoStatus,
        ativo: novoAtivo,
        updatedAt: new Date().toISOString(),
      })
      toast.success(novoStatus === 'inativo' ? `${consultora.nome} desativado(a)` : `${consultora.nome} reativado(a)`)
      carregarConsultoras()
    } catch (error) {
      console.error('Erro ao alterar status do(a) consultor(a):', error)
      toast.error('Erro ao alterar status')
    }
  }

  const copiarLinkConsultora = async (usuario: string) => {
    const link = `https://www.descubraseusoleos.com.br/?c=${usuario}`
    try {
      await copiarTexto(link)
      toast.success('Link copiado!')
    } catch {
      toast.error('Erro ao copiar link')
    }
  }

  // ══════════════════════════════════════════
  // GESTAO DE ACESSOS — CADASTRO/EDICAO COMPLETO
  // ══════════════════════════════════════════

  const limparFormAcesso = () => {
    setAcessoNome('')
    setAcessoEmail('')
    setAcessoWhatsapp('')
    setAcessoUsuario('')
    setAcessoSenha('')
    setAcessoTipo('gratuito')
    setAcessoDataExpiracao('')
    setAcessoStatus('gratuito')
    setAcessoPlano('essencial')
    setEditandoConsultora(null)
  }

  const tipoParaStatus = (tipo: TipoAcesso): StatusAssinatura => {
    if (tipo === 'gratuito') return 'gratuito'
    return 'ativo'
  }

  const mudarTipoAcesso = (novoTipo: TipoAcesso) => {
    setAcessoTipo(novoTipo)
    // preencher data de expiracao automaticamente
    if (novoTipo === 'gratuito') {
      setAcessoDataExpiracao('')
    } else {
      const iso = calcularDataExpiracao(novoTipo)
      setAcessoDataExpiracao(iso ? iso.split('T')[0] : '')
    }
    setAcessoStatus(tipoParaStatus(novoTipo))
  }

  const iniciarEdicaoAcesso = (c: Consultora) => {
    setEditandoConsultora(c)
    setAcessoNome(c.nome || '')
    setAcessoEmail(c.email || '')
    setAcessoWhatsapp(c.whatsapp || '')
    setAcessoUsuario(c.usuario || '')
    setAcessoSenha('')
    setAcessoTipo((c.tipoAcesso as TipoAcesso) || 'gratuito')
    setAcessoStatus((c.statusAssinatura as StatusAssinatura) || 'gratuito')
    setAcessoPlano((c.planoAssinatura as PlanoAssinatura) || 'essencial')
    setAcessoDataExpiracao(c.dataExpiracao ? c.dataExpiracao.split('T')[0] : '')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const salvarAcesso = async (e: React.FormEvent) => {
    e.preventDefault()
    const nome = acessoNome.trim()
    const usuario = acessoUsuario.trim().toLowerCase().replace(/\s+/g, '')
    const senhaInformada = acessoSenha.trim()
    const senhaGerada = editandoConsultora ? '' : gerarSenhaProvisoria()
    const senha = editandoConsultora ? senhaInformada : senhaGerada

    if (!nome || !usuario) {
      toast.error('Nome e usuário são obrigatórios')
      return
    }
    if (editandoConsultora && senhaInformada) {
      toast.error('A senha não pode ser alterada por aqui. A consultora redefine no próprio acesso.')
      return
    }
    if (!editandoConsultora && !acessoEmail.trim()) {
      toast.error('Informe o e-mail para enviar a senha provisória')
      return
    }

    // Usuario unico (exceto ao editar o proprio)
    const duplicado = consultoras.some(
      c => c.usuario === usuario && c._id !== editandoConsultora?._id
    )
    if (duplicado) {
      toast.error('Já existe um(a) consultor(a) com esse usuário')
      return
    }

    const dataExpISO = acessoTipo === 'gratuito' || !acessoDataExpiracao
      ? ''
      : new Date(acessoDataExpiracao + 'T23:59:59.999Z').toISOString()

    const now = new Date().toISOString()
    const payloadBase = {
      nome,
      email: acessoEmail.trim(),
      whatsapp: acessoWhatsapp.trim(),
      usuario,
      ativo: acessoStatus !== 'inativo',
      tipoAcesso: acessoTipo,
      statusAssinatura: acessoStatus,
      planoAssinatura: acessoPlano,
      dataExpiracao: dataExpISO,
      updatedAt: now,
    }

    try {
      if (editandoConsultora) {
        await lumi.entities.consultoras.update(editandoConsultora._id, payloadBase)
        toast.success('Acesso atualizado!')
      } else {
        await lumi.entities.consultoras.create({
          ...payloadBase,
          senha,
          precisaTrocarSenha: true,
          primeiroAcesso: true,
          migradoLegado: false,
          enviouAvisoVencimento3Dias: false,
          exibirOleosResultado: false,
          exibirOleosResultadoKids: false,
          creator: 'admin',
          createdAt: now,
        })
        toast.success('Acesso cadastrado!')
        // Disparar e-mail de boas-vindas automaticamente se houver e-mail
        if (acessoEmail.trim()) {
          try {
            await fetch(EMAIL_FUNCTION_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tipo: 'boas_vindas_equipe',
                nome,
                email: acessoEmail.trim(),
                usuario,
                senha,
              }),
            })
            toast.success('E-mail de boas-vindas enviado para ' + acessoEmail.trim() + '!')
          } catch (emailErr) {
            console.error('Erro ao enviar e-mail de boas-vindas:', emailErr)
            toast.error('Acesso cadastrado, mas houve erro ao enviar o e-mail de boas-vindas.')
          }
        }
      }
      limparFormAcesso()
      carregarConsultoras()
    } catch (error) {
      console.error('Erro ao salvar acesso:', error)
      toast.error('Erro ao salvar acesso')
    }
  }

  // ══════════════════════════════════════════
  // DISTRIBUIDORAS — ACOES
  // ══════════════════════════════════════════

  const cadastrarDistribuidora = async (e: React.FormEvent) => {
    e.preventDefault()
    const nome = novaDistNome.trim()
    const slug = novaDistSlug.trim().toLowerCase().replace(/\s+/g, '')
    const senha = novaDistSenha.trim()
    const total = parseInt(novaDistTotal, 10)

    if (!nome || !slug || !senha || !total) {
      toast.error('Preencha nome, slug, senha e total de acessos')
      return
    }
    if (distribuidoras.some(d => d.slug === slug)) {
      toast.error('Já existe uma distribuidora com esse slug')
      return
    }
    try {
      const now = new Date().toISOString()
      await lumi.entities.distribuidoras.create({
        nome,
        slug,
        email: novaDistEmail.trim(),
        whatsapp: novaDistWhats.trim(),
        senha,
        totalAcessos: total,
        ativo: true,
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      })
      toast.success(`Distribuidora "${nome}" cadastrada!`)
      setNovaDistNome('')
      setNovaDistSlug('')
      setNovaDistEmail('')
      setNovaDistWhats('')
      setNovaDistSenha('')
      setNovaDistTotal('100')
      carregarDistribuidoras()
    } catch (error) {
      console.error('Erro ao cadastrar distribuidora:', error)
      toast.error('Erro ao cadastrar distribuidora')
    }
  }

  const toggleAtivoDist = async (d: Distribuidora) => {
    try {
      await lumi.entities.distribuidoras.update(d._id, {
        ativo: !d.ativo,
        updatedAt: new Date().toISOString(),
      })
      carregarDistribuidoras()
    } catch (e) {
      console.error(e)
      toast.error('Erro ao alterar status')
    }
  }

  const excluirDist = async (id: string) => {
    if (!confirm('Excluir esta distribuidora?')) return
    try {
      await lumi.entities.distribuidoras.delete(id)
      carregarDistribuidoras()
    } catch (e) {
      console.error(e)
      toast.error('Erro ao excluir')
    }
  }

  // consultoras vinculadas a cada distribuidora
  const consultorasPorDistribuidora = useMemo(() => {
    const map: Record<string, Consultora[]> = {}
    consultoras.forEach(c => {
      const slug = c.distribuidora || ''
      if (!slug) return
      if (!map[slug]) map[slug] = []
      map[slug].push(c)
    })
    return map
  }, [consultoras])

  const copiarLinkDist = async (slug: string) => {
    const link = `https://www.descubraseusoleos.com.br/cadastro?d=${slug}`
    try {
      await copiarTexto(link)
      toast.success('Link da distribuidora copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // ══════════════════════════════════════════
  // TELA DE LOGIN
  // ══════════════════════════════════════════

  if (!autenticado) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-verde-muito-claro rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-verde-escuro" />
            </div>
            <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro">
              Painel Administrativo
            </h1>
            <p className="text-sm text-texto-suave mt-2">
              Acesse como administrador(a) ou consultor(a)
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-texto-escuro mb-2 text-sm">Usuário</label>
              <input
                type="text"
                value={loginUsuario}
                onChange={e => setLoginUsuario(e.target.value)}
                className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                placeholder="admin ou usuário do(a) consultor(a)"
              />
            </div>
            <div>
              <label className="block text-texto-escuro mb-2 text-sm">Senha</label>
              <input
                type="password"
                value={loginSenha}
                onChange={e => setLoginSenha(e.target.value)}
                className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                placeholder="Digite a senha"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
            >
              Entrar
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => setModalEsqueciSenha(true)}
              className="text-sm text-verde-escuro hover:underline flex items-center justify-center gap-1.5 mx-auto"
            >
              <KeyRound className="w-3.5 h-3.5" />
              Esqueceu sua senha? Clique aqui
            </button>
          </div>

          <p className="text-xs text-texto-suave text-center mt-4">
            Administrador(a): use "admin" + senha. Consultor(a): use seu usuário + senha.
          </p>
        </div>

        {/* Modal "Esqueceu senha?" */}
        {modalEsqueciSenha && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-verde-muito-claro rounded-full flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-6 h-6 text-verde-escuro" />
                  </div>
                  <h3 className="text-xl font-cormorant font-semibold text-texto-escuro">
                    Redefinir senha
                  </h3>
                </div>
                <button
                  onClick={fecharModalEsqueciSenha}
                  className="text-texto-suave hover:text-texto-escuro"
                  aria-label="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {recuperacaoEnviada ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-green-600" />
                  </div>
                  <p className="text-texto-escuro mb-2 font-medium">E-mail enviado!</p>
                  <p className="text-sm text-texto-suave leading-relaxed">
                    Enviamos um link de redefinição para <strong>{emailRecuperacao}</strong>.
                    O link é válido por <strong>24 horas</strong>.
                  </p>
                  <p className="text-xs text-texto-suave mt-3">
                    Se não encontrar verifique também a caixa de spam.
                  </p>
                  <button
                    onClick={fecharModalEsqueciSenha}
                    className="mt-5 w-full bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
                  >
                    Voltar ao login
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-texto-suave mb-4 leading-relaxed">
                    Informe o e-mail cadastrado na sua conta. Enviaremos um link para criar uma nova senha.
                  </p>
                  <form onSubmit={solicitarRedefinicao} className="space-y-4">
                    <div>
                      <label className="block text-sm text-texto-suave mb-2 font-medium flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />
                        E-mail cadastrado
                      </label>
                      <input
                        type="email"
                        value={emailRecuperacao}
                        onChange={e => setEmailRecuperacao(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                        placeholder="seu@email.com.br"
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={fecharModalEsqueciSenha}
                        className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={enviandoRecuperacao}
                        className="flex-1 px-4 py-3 bg-verde-escuro text-white rounded-xl hover:bg-opacity-90 transition-colors font-medium disabled:opacity-60"
                      >
                        {enviandoRecuperacao ? 'Enviando...' : 'Enviar link'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (precisaTrocarSenha && perfilLogado?.tipo === 'consultora') {
    return (
      <TrocaSenhaObrigatoria
        consultoraId={perfilLogado._id}
        nome={perfilLogado.nome}
        onConcluido={() => setPrecisaTrocarSenha(false)}
      />
    )
  }

  // ══════════════════════════════════════════
  // PÁGINA DE PERFIL DA CONSULTORA
  // ══════════════════════════════════════════

  // O botão "Meu perfil" do header e a rota /perfil usam a aba completa (MeuPerfilAba), com foto.

  // ══════════════════════════════════════════
  // PAINEL PRINCIPAL
  // ══════════════════════════════════════════

  return (
    <div className="min-h-screen bg-bege-fundo">
      {/* Header */}
      <div className="bg-verde-escuro text-white px-6 py-5">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-2xl font-cormorant font-semibold">
              Painel Administrativo
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {isAdmin ? (
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 px-3 py-1 rounded-full">
                  <Shield className="w-3.5 h-3.5" />
                  Administrador(a)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 px-3 py-1 rounded-full">
                  <UserIcon className="w-3.5 h-3.5" />
                  {perfilLogado?.tipo === 'consultora' ? perfilLogado.nome : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isAdmin && quantidadeLeadsNovos > 0 && (
              <button
                onClick={irParaLeads}
                className="relative flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-full text-sm font-medium transition-all"
                title={`${quantidadeLeadsNovos} novo(s) contato(s)`}
              >
                <Bell className="w-4 h-4" />
                {quantidadeLeadsNovos} novo{quantidadeLeadsNovos > 1 ? 's' : ''}
              </button>
            )}
            {perfilLogado?.tipo === 'consultora' && (
              <button
                onClick={() => {
                  setAbaAtiva('perfil')
                  setMostrandoPerfil(false)
                  if (window.location.pathname === '/perfil') {
                    window.history.replaceState({}, '', '/admin')
                  }
                }}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full transition-all text-sm font-medium"
                title="Meu perfil"
              >
                <UserIcon className="w-4 h-4" />
                Meu perfil
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-white text-verde-escuro px-4 py-2 rounded-full hover:bg-opacity-90 transition-all text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="bg-white border-b border-bege-medio">
        <div className="container mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          <button
            onClick={irParaLeads}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${abaAtiva === 'leads' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            Contatos
            {quantidadeLeadsNovos > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full">
                {quantidadeLeadsNovos > 9 ? '9+' : quantidadeLeadsNovos}
              </span>
            )}
          </button>
          <button
            onClick={irParaLeadsKids}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'leads_kids' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Baby className="w-4 h-4" />
            Contatos Infantis
          </button>
          <button
            onClick={() => setAbaAtiva('divulgar')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'divulgar' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Megaphone className="w-4 h-4" />
            Como Divulgar
          </button>
          <button
            onClick={() => setAbaAtiva('calculadora')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'calculadora' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Calculator className="w-4 h-4" />
            Calculadora de Óleos Essenciais
          </button>
          <button
            onClick={() => setAbaAtiva('protocolos')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'protocolos' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <FileText className="w-4 h-4" />
            Protocolos de Óleos Essenciais
          </button>
          <button
            onClick={() => setAbaAtiva('inscritos')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'inscritos' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <UserPlus className="w-4 h-4" />
            Cursos de Aromaterapia
          </button>
          <button
            onClick={() => setAbaAtiva('vivencias')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'vivencias' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Sparkles className="w-4 h-4" />
            Vivências Terapêuticas
          </button>
          <button
            onClick={() => setAbaAtiva('como_crescer')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'como_crescer' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Rocket className="w-4 h-4" />
            Como Crescer
          </button>
          <button
            onClick={() => setAbaAtiva('empresas')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'empresas' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <Building2 className="w-4 h-4" />
            Empresas
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setAbaAtiva('consultoras')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'consultoras' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
              >
                <Users className="w-4 h-4" />
                Consultoras
              </button>
              <button
                onClick={() => setAbaAtiva('acessos')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'acessos' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
              >
                <Crown className="w-4 h-4" />
                Gestão de Acessos
              </button>
              <button
                onClick={() => setAbaAtiva('distribuidoras')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'distribuidoras' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
              >
                <Package className="w-4 h-4" />
                Distribuidoras
              </button>
              <button
                onClick={() => setAbaAtiva('configuracoes')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'configuracoes' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
              >
                <Settings className="w-4 h-4" />
                Configurações
              </button>
              <button
                onClick={() => setAbaAtiva('mentoria')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'mentoria' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
              >
                <HeartHandshake className="w-4 h-4" />
                Mentoria
              </button>
            </>
          )}
          {/* ÚLTIMA ABA — Como Usar o Sistema (treinamento) */}
          <button
            onClick={() => setAbaAtiva('treinamento')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${abaAtiva === 'treinamento' ? 'border-verde-escuro text-verde-escuro' : 'border-transparent text-texto-suave hover:text-texto-escuro'}`}
          >
            <GraduationCap className="w-4 h-4" />
            Como Usar o Sistema
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* ═══════════════════════════════════ */}
        {/* ABA: LEADS                         */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'leads' && isAdmin && (
          <>
            <div className="bg-[#FEF9E7] border border-[#F5E1A4] rounded-2xl p-4 mb-6 text-sm text-[#7A5C00] leading-relaxed flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Por privacidade, a administradora visualiza apenas totais agregados por consultora. Respostas individuais dos diagnósticos e dados pessoais dos contatos (nome, WhatsApp, e-mail) não são exibidos aqui.</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-verde-escuro mb-1">{stats.total}</div>
                <div className="text-sm text-texto-suave">Total de contatos</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-blue-600 mb-1">{stats.novo}</div>
                <div className="text-sm text-texto-suave">Novo contato</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-green-600 mb-1">{stats.convertido}</div>
                <div className="text-sm text-texto-suave">Convertido</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-red-600 mb-1">{stats.naoRespondeu}</div>
                <div className="text-sm text-texto-suave">Não respondeu</div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-bege-medio">
                <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                  <Users className="w-5 h-5 text-verde-escuro" />
                  Diagnósticos adulto por consultora
                </h2>
              </div>
              {breakdownAdultoPorConsultora.length === 0 ? (
                <div className="p-8 text-center text-texto-suave">Nenhum contato registrado ainda.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bege-fundo">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultora</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Total</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Novo</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Em contato</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Agendado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Convertido</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Não respondeu</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Não qualificado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bege-medio">
                      {breakdownAdultoPorConsultora.map(b => (
                        <tr key={b.usuario} className="hover:bg-bege-fundo">
                          <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{b.nome}</td>
                          <td className="px-4 py-3 text-sm text-texto-escuro font-semibold">{b.total}</td>
                          <td className="px-4 py-3 text-sm text-blue-600">{b.novo}</td>
                          <td className="px-4 py-3 text-sm text-yellow-600">{b.contato}</td>
                          <td className="px-4 py-3 text-sm text-purple-600">{b.agendado}</td>
                          <td className="px-4 py-3 text-sm text-green-600">{b.convertido}</td>
                          <td className="px-4 py-3 text-sm text-red-600">{b.naoRespondeu}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{b.naoQualificado}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {abaAtiva === 'leads' && !isAdmin && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-verde-escuro mb-1">{stats.total}</div>
                <div className="text-sm text-texto-suave">Total de contatos</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-blue-600 mb-1">{stats.novo}</div>
                <div className="text-sm text-texto-suave">Novo contato</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-yellow-600 mb-1">{stats.contato}</div>
                <div className="text-sm text-texto-suave">Em contato</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-purple-600 mb-1">{stats.agendado}</div>
                <div className="text-sm text-texto-suave">Agendado</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-green-600 mb-1">{stats.convertido}</div>
                <div className="text-sm text-texto-suave">Convertido</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-red-600 mb-1">{stats.naoRespondeu}</div>
                <div className="text-sm text-texto-suave">Não respondeu</div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-gray-600 mb-1">{stats.naoQualificado}</div>
                <div className="text-sm text-texto-suave">Não qualificado</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Status</label>
                  <select
                    value={filtroStatus}
                    onChange={e => setFiltroStatus(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  >
                    <option value="todos">Todos</option>
                    <option value="Novo lead">Novo contato</option>
                    <option value="Em contato">Em contato</option>
                    <option value="Agendado">Agendado</option>
                    <option value="Convertido">Convertido</option>
                    <option value="Não respondeu">Não respondeu</option>
                    <option value="Não qualificado">Não qualificado</option>
                  </select>
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm text-texto-suave mb-2">Consultora</label>
                    <select
                      value={filtroConsultora}
                      onChange={e => setFiltroConsultora(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                    >
                      <option value="todos">Todos</option>
                      {consultorasUnicas.map(c => (
                        <option key={c} value={c}>
                          {c} ({leadsCountPorConsultora[c] || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {empresasUnicas.length > 0 && (
                  <div>
                    <label className="block text-sm text-texto-suave mb-2">Por empresa</label>
                    <select
                      value={filtroEmpresa}
                      onChange={e => setFiltroEmpresa(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                    >
                      <option value="todos">Todas</option>
                      {empresasUnicas.map(slug => (
                        <option key={slug} value={slug}>
                          {empresasNomes[slug] || slug}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Buscar</label>
                  <input
                    type="text"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Nome ou WhatsApp"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={exportarCSV}
                    className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2 rounded-xl hover:bg-opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Exportar CSV
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-texto-suave">Carregando...</div>
              ) : leadsFiltrados.length === 0 ? (
                <div className="p-8 text-center text-texto-suave">Nenhum contato encontrado</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bege-fundo">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Data/Hora</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">WhatsApp</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">País</th>
                        {isAdmin && (
                          <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultor(a)</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Óleos</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro" style={{ minWidth: '200px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bege-medio">
                      {leadsFiltrados.map(lead => {
                        const isNovo = leadsNovosIds.has(lead._id) && !leadsNovosVistos.has(lead._id)
                        return (
                        <tr key={lead._id} className={`hover:bg-bege-fundo ${isNovo ? 'bg-green-50 border-l-4 border-green-500' : ''}`}>
                          <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {leadsNovosIds.has(lead._id) && !leadsNovosVistos.has(lead._id) && (
                                <span className="px-1.5 py-0.5 bg-green-500 text-white text-xs font-bold rounded">NOVO</span>
                              )}
                              {new Date(lead.data_hora).toLocaleString('pt-BR')}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{lead.nome}</td>
                          <td className="px-4 py-3 text-sm">
                            <a
                              href={`https://wa.me/${formatarNumeroWhatsApp(lead.whatsapp)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-verde-escuro hover:underline"
                            >
                              {exibirWhatsAppFormatado(lead.whatsapp, lead.pais)}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-sm text-texto-suave">{lead.email}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${lead.pais === 'Portugal' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                              {lead.pais === 'Portugal' ? '🇵🇹 Portugal' : lead.pais === 'Estados Unidos' ? '🇺🇸 EUA' : lead.pais === 'Outro' ? '🌍 Outro' : '🇧🇷 Brasil'}
                            </span>
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                {lead.consultora || 'geral'}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm text-texto-suave">
                            {(lead.oleos_indicados || []).length} óleos
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="relative inline-block">
                              <select
                                value={lead.status}
                                onChange={e => atualizarStatus(lead._id, e.target.value)}
                                className="appearance-none px-4 py-2 pr-8 rounded-full text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-verde-claro"
                                style={{
                                  backgroundColor: obterCoresStatus(lead.status).bg,
                                  color: obterCoresStatus(lead.status).text,
                                }}
                              >
                                <option value="Novo lead">Novo contato</option>
                                <option value="Em contato">Em contato</option>
                                <option value="Agendado">Agendado</option>
                                <option value="Convertido">Convertido</option>
                                <option value="Não respondeu">Não respondeu</option>
                                <option value="Não qualificado">Não qualificado</option>
                              </select>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <div className="flex flex-row items-center gap-2">
                              <button
                                onClick={() => {
                                  setLeadSelecionado(lead)
                                  setModalAberto(true)
                                  // Marcar como visto ao abrir modal
                                  setLeadsNovosVistos(prev => new Set([...prev, lead._id]))
                                }}
                                className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors"
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => abrirWhatsAppResumo(lead)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Abrir no WhatsApp"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => excluirLead(lead._id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: LEADS KIDS                    */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'leads_kids' && (() => {
          const NOME_FAIXA_KIDS: Record<string, string> = {
            BEBE: 'Bebê (0-2 anos)',
            PRE_ESCOLAR: 'Pré-escolar (3-6 anos)',
            CRIANCA: 'Criança (7-12 anos)',
            ADOLESCENTE: 'Adolescente (13-17 anos)',
          }
          const PRECOS_MIXES_K: Record<string, { preco: number; pv: number }> = {
            CALMER: { preco: 150.0, pv: 20 }, THINKER: { preco: 127.5, pv: 17 },
            BRAVE: { preco: 165.0, pv: 22 }, STEADY: { preco: 120.0, pv: 16 },
            STRONGER: { preco: 112.5, pv: 15 }, TAMER: { preco: 112.5, pv: 15 },
            RESCUER: { preco: 120.0, pv: 16 },
          }
          const PRECOS_TOUCH_K: Record<string, { preco: number; pv: number }> = {
            'Lavanda Touch': { preco: 120.0, pv: 16 }, 'Vetiver Touch': { preco: 247.5, pv: 33 },
            'Breathe Touch': { preco: 112.5, pv: 15 }, 'On Guard Touch': { preco: 180.0, pv: 24 },
            'Adaptiv Touch': { preco: 150.0, pv: 20 }, 'Hortelã-Pimenta Touch': { preco: 112.5, pv: 15 },
            'Zengest Touch': { preco: 172.5, pv: 23 },
          }
          const NOME_MIX_K: Record<string, { emoji: string; nomePort: string; descricao: string; locais: string }> = {
            CALMER: { emoji: '🌙', nomePort: 'Mix para Serenidade', descricao: 'sono tranquilo e calma', locais: 'Sob o nariz, testa, coluna ou sola dos pés' },
            THINKER: { emoji: '🧠', nomePort: 'Mix para Foco', descricao: 'concentração e aprendizagem', locais: 'Sob o nariz, testa, nuca, peito, coluna ou sola dos pés' },
            BRAVE: { emoji: '🦁', nomePort: 'Mix para Coragem', descricao: 'autoconfiança e superação', locais: 'Sob o nariz, nuca, testa, peito, estômago, coluna ou sola dos pés' },
            STEADY: { emoji: '⚖️', nomePort: 'Mix para Equilíbrio', descricao: 'humor estável e bem-estar', locais: 'Sob o nariz, nuca, testa, peito, coluna ou sola dos pés' },
            STRONGER: { emoji: '🛡️', nomePort: 'Mix Protetor', descricao: 'imunidade e proteção', locais: 'Sob o nariz, peito, abdome, coluna ou sola dos pés' },
            TAMER: { emoji: '🫧', nomePort: 'Mix Digestivo', descricao: 'conforto respiratório e digestivo', locais: 'Sob o nariz, peito, abdome, coluna ou sola dos pés' },
            RESCUER: { emoji: '💙', nomePort: 'Mix para Conforto', descricao: 'alívio de tensão e dores', locais: 'Sob o nariz, nuca, peito, ombros, coluna ou sola dos pés' },
          }

          const KIT_COMPLETO_K = { preco: 766.25, pv: 89 }
          const DIFUSOR_PETAL_K = { preco: 498.75, pv: 30 }
          const FRETE_K = 32.0
          const TAXA_ADESAO_K = 48.0
          const PV_MIN_ISENCAO_K = 100

          const obterCoresStatusK = (status: string) => {
            const cores: Record<string, { bg: string; text: string }> = {
              'Novo lead': { bg: '#E6F1FB', text: '#185FA5' }, 'Em contato': { bg: '#FAEEDA', text: '#854F0B' },
              'Agendado': { bg: '#F3E8FF', text: '#6B21A8' }, 'Convertido': { bg: '#D8F3DC', text: '#2D6A4F' },
              'Não respondeu': { bg: '#FCEBEB', text: '#A32D2D' }, 'Não qualificado': { bg: '#F3F4F6', text: '#4B5563' },
            }
            return cores[status] || { bg: '#F3F4F6', text: '#6B7280' }
          }

          const calcParcK = (totalComFrete: number) => {
            if (totalComFrete >= 600) return { avista: totalComFrete, parcelas: [{ n: 3, valor: totalComFrete / 3 }, { n: 6, valor: totalComFrete / 6 }] }
            if (totalComFrete >= 300) return { avista: totalComFrete, parcelas: [{ n: 3, valor: totalComFrete / 3 }] }
            return { avista: totalComFrete, parcelas: null }
          }

          const leadsKidsVisiveis = isAdmin ? leadsKids
            : consultoraLogada ? leadsKids.filter(l => l.consultora === consultoraLogada)
            : []

          const leadsKidsFiltrados = leadsKidsVisiveis.filter(l => {
            const ms = filtroStatusKids === 'todos' || l.status === filtroStatusKids
            const mf = filtroFaixaKids === 'todos' || l.faixaEtaria === filtroFaixaKids
            const mc = filtroConsultoraKids === 'todos' || (l.consultora || 'geral') === filtroConsultoraKids
            const mb = !buscaKids || (l.nomeMae || '').toLowerCase().includes(buscaKids.toLowerCase())
              || (l.nomeKid || '').toLowerCase().includes(buscaKids.toLowerCase())
              || (l.whatsapp || '').includes(buscaKids)
            return ms && mf && mc && mb
          })

          const statsK = {
            total: leadsKidsVisiveis.length,
            novo: leadsKidsVisiveis.filter(l => l.status === 'Novo lead').length,
            contato: leadsKidsVisiveis.filter(l => l.status === 'Em contato').length,
            convertido: leadsKidsVisiveis.filter(l => l.status === 'Convertido').length,
            bebe: leadsKidsVisiveis.filter(l => l.faixaEtaria === 'BEBE').length,
            preEscolar: leadsKidsVisiveis.filter(l => l.faixaEtaria === 'PRE_ESCOLAR').length,
            crianca: leadsKidsVisiveis.filter(l => l.faixaEtaria === 'CRIANCA').length,
            adolescente: leadsKidsVisiveis.filter(l => l.faixaEtaria === 'ADOLESCENTE').length,
          }

          const atualizarStatusKid = async (id: string, status: string) => {
            try {
              await lumi.entities.leads_kids.update(id, { status, updatedAt: new Date().toISOString() })
              toast.success('Status atualizado!')
              carregarLeadsKids()
              if (leadKidsSelecionado?._id === id) setLeadKidsSelecionado((prev: any) => prev ? { ...prev, status } : prev)
            } catch { toast.error('Erro ao atualizar status') }
          }

          const excluirLeadKid = async (id: string) => {
            if (!confirm('Excluir este lead kids?')) return
            try {
              await lumi.entities.leads_kids.delete(id)
              toast.success('Lead excluído!')
              carregarLeadsKids()
              setLeadKidsSelecionado(null)
            } catch { toast.error('Erro ao excluir') }
          }

          const exportarCSVKids = () => {
            const headers = ['Data', 'Mãe/Responsável', 'WhatsApp', 'Criança', 'Idade', 'Faixa', 'País', 'Consultora', 'Mixes', 'Status']
            const rows = leadsKidsFiltrados.map(l => [
              l.data_hora ? new Date(l.data_hora).toLocaleString('pt-BR') : '',
              l.nomeMae, l.whatsapp, l.nomeKid, l.idadeKid,
              NOME_FAIXA_KIDS[l.faixaEtaria] || l.faixaEtaria,
              l.pais, l.consultora || 'geral',
              (l.mixesIndicados || []).join('; '), l.status,
            ])
            const csv = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n')
            const a = document.createElement('a')
            a.setAttribute('href', encodeURI(csv))
            a.setAttribute('download', `leads_kids_${new Date().toISOString().split('T')[0]}.csv`)
            document.body.appendChild(a); a.click(); document.body.removeChild(a)
          }

          const consultorasUnicasK = [...new Set(leadsKids.map(l => l.consultora || 'geral'))].sort()

          // Para a administradora: visão agregada por consultora, sem dados individuais (privacidade)
          if (isAdmin) {
            return (
              <>
                <div className="bg-[#FEF9E7] border border-[#F5E1A4] rounded-2xl p-4 mb-6 text-sm text-[#7A5C00] leading-relaxed flex items-start gap-2">
                  <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Por privacidade, a administradora visualiza apenas totais agregados por consultora. Respostas individuais dos diagnósticos infantis e dados pessoais dos contatos (mãe/responsável, criança, WhatsApp) não são exibidos aqui.</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white p-5 rounded-xl shadow-md">
                    <div className="text-3xl font-bold text-verde-escuro mb-1">{statsK.total}</div>
                    <div className="text-sm text-texto-suave">Total de contatos infantis</div>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-md">
                    <div className="text-3xl font-bold text-blue-600 mb-1">{statsK.novo}</div>
                    <div className="text-sm text-texto-suave">Novo contato</div>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-md">
                    <div className="text-3xl font-bold text-green-600 mb-1">{statsK.convertido}</div>
                    <div className="text-sm text-texto-suave">Convertido</div>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-md">
                    <div className="text-3xl font-bold text-texto-suave mb-1">{Math.max(0, statsK.total - statsK.novo - statsK.contato - statsK.convertido)}</div>
                    <div className="text-sm text-texto-suave">Outros status</div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-bege-medio">
                    <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                      <Baby className="w-5 h-5 text-verde-escuro" />
                      Diagnósticos Kids por consultora
                    </h2>
                  </div>
                  {breakdownKidsPorConsultora.length === 0 ? (
                    <div className="p-8 text-center text-texto-suave">Nenhum contato infantil registrado ainda.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-bege-fundo">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultora</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Total</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Novo</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Em contato</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Convertido</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-bege-medio">
                          {breakdownKidsPorConsultora.map(b => (
                            <tr key={b.usuario} className="hover:bg-bege-fundo">
                              <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{b.nome}</td>
                              <td className="px-4 py-3 text-sm text-texto-escuro font-semibold">{b.total}</td>
                              <td className="px-4 py-3 text-sm text-blue-600">{b.novo}</td>
                              <td className="px-4 py-3 text-sm text-yellow-600">{b.contato}</td>
                              <td className="px-4 py-3 text-sm text-green-600">{b.convertido}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )
          }

          return (
            <>
              {/* Métricas */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
                {[
                  { label: 'Total', valor: statsK.total, cor: 'text-[#2C2C2A]' },
                  { label: 'Novo contato', valor: statsK.novo, cor: 'text-blue-600' },
                  { label: 'Em contato', valor: statsK.contato, cor: 'text-yellow-600' },
                  { label: 'Convertido', valor: statsK.convertido, cor: 'text-[#2D6A4F]' },
                  { label: 'Bebê', valor: statsK.bebe, cor: 'text-pink-600' },
                  { label: 'Pré-escolar', valor: statsK.preEscolar, cor: 'text-orange-600' },
                  { label: 'Criança', valor: statsK.crianca, cor: 'text-indigo-600' },
                  { label: 'Adolescente', valor: statsK.adolescente, cor: 'text-purple-600' },
                ].map(m => (
                  <div key={m.label} className="bg-white p-4 rounded-xl shadow-md text-center">
                    <div className={`text-2xl font-bold mb-1 ${m.cor}`}>{m.valor}</div>
                    <div className="text-xs text-[#5F5E5A]">{m.label}</div>
                  </div>
                ))}
              </div>

              {/* Filtros */}
              <div className="bg-white p-5 rounded-xl shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-xs text-texto-suave mb-1">Status</label>
                    <select value={filtroStatusKids} onChange={e => setFiltroStatusKids(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:outline-none text-sm">
                      <option value="todos">Todos</option>
                      <option value="Novo lead">Novo contato</option>
                      <option value="Em contato">Em contato</option>
                      <option value="Agendado">Agendado</option>
                      <option value="Convertido">Convertido</option>
                      <option value="Não respondeu">Não respondeu</option>
                      <option value="Não qualificado">Não qualificado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-texto-suave mb-1">Faixa Etária</label>
                    <select value={filtroFaixaKids} onChange={e => setFiltroFaixaKids(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:outline-none text-sm">
                      <option value="todos">Todas</option>
                      <option value="BEBE">Bebê</option>
                      <option value="PRE_ESCOLAR">Pré-escolar</option>
                      <option value="CRIANCA">Criança</option>
                      <option value="ADOLESCENTE">Adolescente</option>
                    </select>
                  </div>
                  {isAdmin && (
                    <div>
                      <label className="block text-xs text-texto-suave mb-1">Consultora</label>
                      <select value={filtroConsultoraKids} onChange={e => setFiltroConsultoraKids(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:outline-none text-sm">
                        <option value="todos">Todas</option>
                        {consultorasUnicasK.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-texto-suave mb-1">Buscar</label>
                    <input type="text" value={buscaKids} onChange={e => setBuscaKids(e.target.value)}
                      placeholder="Nome mãe, criança ou WhatsApp"
                      className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:outline-none text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={exportarCSVKids}
                      className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-4 py-2 rounded-xl hover:bg-[#245A42] transition-all text-sm">
                      <Download className="w-4 h-4" /> CSV
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabela */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {loadingKids ? (
                  <div className="p-8 text-center text-texto-suave">Carregando...</div>
                ) : leadsKidsFiltrados.length === 0 ? (
                  <div className="p-8 text-center text-texto-suave">Nenhum contato infantil encontrado</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-bege-fundo">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Data</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Mãe/Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">WhatsApp</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Criança</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Faixa</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Mixes</th>
                          {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Consultora</th>}
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-bege-medio">
                        {leadsKidsFiltrados.map(lead => (
                          <tr key={lead._id} className="hover:bg-bege-fundo">
                            <td className="px-4 py-3 text-xs text-texto-suave whitespace-nowrap">
                              {lead.data_hora ? new Date(lead.data_hora).toLocaleString('pt-BR') : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{lead.nomeMae}</td>
                            <td className="px-4 py-3 text-sm">
                              <a href={`https://wa.me/${(lead.whatsapp || '').replace(/\D/g, '')}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-verde-escuro hover:underline text-xs">{lead.whatsapp}</a>
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-escuro">
                              <div className="font-medium">{lead.nomeKid}</div>
                              <div className="text-xs text-texto-suave">{lead.idadeKid}</div>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <span className={`px-2 py-1 rounded-full font-medium ${
                                lead.faixaEtaria === 'BEBE' ? 'bg-pink-100 text-pink-700'
                                : lead.faixaEtaria === 'PRE_ESCOLAR' ? 'bg-orange-100 text-orange-700'
                                : lead.faixaEtaria === 'CRIANCA' ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-purple-100 text-purple-700'
                              }`}>
                                {lead.faixaEtaria === 'BEBE' ? '👶 Bebê'
                                  : lead.faixaEtaria === 'PRE_ESCOLAR' ? '🧒 Pré-escolar'
                                  : lead.faixaEtaria === 'CRIANCA' ? '👦 Criança'
                                  : '🧑 Adolescente'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-texto-suave">
                              {(lead.mixesIndicados || []).length} mixes
                            </td>
                            {isAdmin && (
                              <td className="px-4 py-3 text-xs">
                                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full">{lead.consultora || 'geral'}</span>
                              </td>
                            )}
                            <td className="px-4 py-3 text-xs">
                              <select
                                value={lead.status || 'Novo lead'}
                                onChange={e => atualizarStatusKid(lead._id, e.target.value)}
                                className="appearance-none px-3 py-1.5 pr-6 rounded-full text-xs font-medium cursor-pointer focus:outline-none"
                                style={{ backgroundColor: obterCoresStatusK(lead.status).bg, color: obterCoresStatusK(lead.status).text }}
                              >
                                <option value="Novo lead">Novo contato</option>
                                <option value="Em contato">Em contato</option>
                                <option value="Agendado">Agendado</option>
                                <option value="Convertido">Convertido</option>
                                <option value="Não respondeu">Não respondeu</option>
                                <option value="Não qualificado">Não qualificado</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => setLeadKidsSelecionado(lead)}
                                  className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors" title="Ver detalhes">
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => excluirLeadKid(lead._id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Modal Lead Kids */}
              {leadKidsSelecionado && (() => {
                const lk = leadKidsSelecionado
                const nomeMaeD = (lk.nomeMae || '').split(' ')[0]
                const nomeKidD = (lk.nomeKid || '').split(' ')[0] || 'a criança'
                const mixes: string[] = lk.mixesIndicados || []
                const touch: string[] = lk.oleosTouch || []
                const prioridades: string[] = lk.prioridades || []
                const mixesTop3 = mixes.slice(0, 3)
                const conheceOleos: string = lk.conheceOleos || ''
                const cadastradaDoterra: string = lk.cadastradaDoterra || ''

                const badgeAbordagem = (() => {
                  if (cadastradaDoterra === 'Sou cadastrada e estou ativa') return { cor: 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]', label: '🔴 Já cadastrada', orientacao: 'Já é cliente dōTERRA. Verificar se pode atender ou se há vínculo com outra consultora. Abordagem diferente necessária.' }
                  if (cadastradaDoterra === 'Sou cadastrada mas não estou ativa') return { cor: 'bg-[#FEF3C7] text-[#854F0B] border-[#FCD34D]', label: '🟡 Verificar vínculo', orientacao: 'Atenção! Verificar o vínculo com outra consultora antes de prosseguir com a venda.' }
                  // Não é cadastrada — badge verde com orientação por conhecimento prévio
                  let orientacao = 'Tom educativo. Comece explicando o que são os óleos essenciais antes de apresentar os produtos.'
                  if (conheceOleos === 'Já ouvi falar mas nunca usei') orientacao = 'Já tem curiosidade. Foque nos benefícios específicos para ' + nomeKidD + ' sem precisar explicar o básico.'
                  if (conheceOleos === 'Já usei outros óleos essenciais') orientacao = 'Já conhece óleos. Destaque os diferenciais da dōTERRA e da linha Kids especificamente.'
                  if (conheceOleos === 'Já usei óleos dōTERRA') orientacao = 'Lead quente! Já conhece a marca. Vá direto para os mixes indicados e valores.'
                  return { cor: 'bg-[#D8F3DC] text-[#2D6A4F] border-[#A8DFC4]', label: '🟢 Lead livre', orientacao }
                })()

                const gerarTextoOpcao = (numero: number, titulo: string, corpo: string, totalComFrete: number, pv: number, taxa: number, parcelas: { n: number; valor: number }[] | null) => {
                  const fmtBR = (v: number) => v.toFixed(2).replace('.', ',')
                  const produtos = totalComFrete - FRETE_K
                  const totalGeral = totalComFrete + taxa
                  const taxaLinha = taxa === 0 ? '- Taxa de adesão: Isenta 🎉' : `- Taxa de adesão: R$${fmtBR(taxa)}`
                  const parcelStr = parcelas ? `- ${parcelas.map(p => `${p.n}x de R$${fmtBR(p.valor)}`).join(' ou ')} no cartão\n- Ou à vista no Pix/boleto` : '- À vista no Pix/boleto'
                  return `*Opção ${numero} — ${titulo}*\n\n${corpo}\n\n*${pv} PV*\n\n*Investimento:*\n- Produtos: R$${fmtBR(produtos)}\n${taxaLinha}\n- Frete: R$${fmtBR(FRETE_K)}\n- *Total: R$${fmtBR(totalGeral)}*\n${parcelStr}`
                }

                // Cálculos de compra
                const totalPrioritarioProd = mixesTop3.reduce((a, m) => a + (PRECOS_MIXES_K[m]?.preco || 0), 0)
                const totalPrioritario = totalPrioritarioProd + FRETE_K
                const pvPrioritario = mixesTop3.reduce((a, m) => a + (PRECOS_MIXES_K[m]?.pv || 0), 0)
                const taxaPrioritario = pvPrioritario >= PV_MIN_ISENCAO_K ? 0 : TAXA_ADESAO_K
                const parcPrioritario = calcParcK(totalPrioritario + taxaPrioritario)
                const totalPremiumProd = KIT_COMPLETO_K.preco + DIFUSOR_PETAL_K.preco
                const totalPremium = totalPremiumProd + FRETE_K
                const pvPremium = KIT_COMPLETO_K.pv + DIFUSOR_PETAL_K.pv
                const taxaPremium = pvPremium >= PV_MIN_ISENCAO_K ? 0 : TAXA_ADESAO_K
                const parcPremium = calcParcK(totalPremium + taxaPremium)
                const totalCompleto = KIT_COMPLETO_K.preco + FRETE_K
                const taxaCompleto = KIT_COMPLETO_K.pv >= PV_MIN_ISENCAO_K ? 0 : TAXA_ADESAO_K
                const parcCompleto = calcParcK(totalCompleto + taxaCompleto)

                const mixesTexto = mixes.map(m => `${NOME_MIX_K[m]?.emoji || ''} *${NOME_MIX_K[m]?.nomePort || m}* — ${NOME_MIX_K[m]?.descricao || ''}`).join('\n')
                const formatarMixComIngles = (m: string) => NOME_MIX_K[m]?.nomePort ? `${NOME_MIX_K[m].nomePort} (${m.charAt(0) + m.slice(1).toLowerCase()})` : m
                const mixesTop3ComIngles = mixesTop3.map(formatarMixComIngles).join(', ')
                const prioridadesTexto = prioridades.join(', ') || 'bem-estar geral'

                const s1 = `Olá, ${nomeMaeD}! 🌿\n\nRecebi o diagnóstico de saúde e bem-estar de ${nomeKidD} e já analisei as respostas com cuidado.\n\nCom base em tudo que você respondeu, esses são os óleos essenciais indicados para ${nomeKidD} agora:\n\n${mixesTexto}\n\nSão todos roll-on prontos para usar sem precisar diluir e 100% seguros para crianças.\n\nVi que ${nomeKidD} precisa principalmente de: ${prioridadesTexto}. Isso me diz muito sobre o que ela precisa agora.`
                const s2 = `Vamos agendar um momento para eu te apresentar como você pode iniciar com ${nomeKidD} e tirar todas as suas dúvidas. Qual o melhor horário para você? 📅`

                const copiarK = async (texto: string) => {
                  try { await copiarTexto(texto); toast.success('Copiado!') }
                  catch { toast.error('Erro ao copiar') }
                }

                return (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
                      {/* Header */}
                      <div className="sticky top-0 bg-[#2D6A4F] text-white px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
                        <div>
                          <h2 className="text-2xl font-cormorant font-semibold flex items-center gap-2">
                            <Baby className="w-5 h-5" /> Contato Infantil — {lk.nomeKid}
                          </h2>
                          <p className="text-[#A8DFC4] text-sm mt-0.5">{lk.nomeMae} · {NOME_FAIXA_KIDS[lk.faixaEtaria] || lk.faixaEtaria}</p>
                        </div>
                        <button onClick={() => setLeadKidsSelecionado(null)} className="text-white hover:text-gray-200">
                          <X className="w-6 h-6" />
                        </button>
                      </div>

                      <div className="p-6 space-y-8">
                        {/* SEÇÃO 1 — Dados básicos */}
                        <section>
                          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">1. Dados Básicos</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                              { label: 'Mãe/Responsável', valor: lk.nomeMae },
                              { label: 'WhatsApp', valor: lk.whatsapp },
                              { label: 'Nome da criança', valor: lk.nomeKid },
                              { label: 'Idade', valor: lk.idadeKid },
                              { label: 'Faixa etária', valor: NOME_FAIXA_KIDS[lk.faixaEtaria] || lk.faixaEtaria },
                              { label: 'País', valor: lk.pais },
                              { label: 'Data', valor: lk.data_hora ? new Date(lk.data_hora).toLocaleString('pt-BR') : '—' },
                              { label: 'Consultora', valor: lk.consultora || 'geral' },
                            ].map(({ label, valor }) => (
                              <div key={label}>
                                <div className="text-xs text-texto-suave mb-1 font-medium">{label}</div>
                                <div className="text-texto-escuro text-sm">{valor || '—'}</div>
                              </div>
                            ))}
                          </div>
                        </section>

                        {/* SEÇÃO 2 — Orientação de Abordagem (logo após dados básicos) */}
                        {(conheceOleos || cadastradaDoterra) && (
                          <section>
                            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">2. Orientação de Abordagem</h3>
                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border mb-3 ${badgeAbordagem.cor}`}>
                              {badgeAbordagem.label}
                            </div>
                            {conheceOleos && <div className="text-xs text-texto-suave mb-1">Conhece óleos: <strong>{conheceOleos}</strong></div>}
                            {cadastradaDoterra && <div className="text-xs text-texto-suave mb-2">Cadastro dōTERRA: <strong>{cadastradaDoterra}</strong></div>}
                            <div className="bg-bege-fundo border border-bege-medio rounded-xl px-4 py-3 text-sm text-texto-escuro">{badgeAbordagem.orientacao}</div>
                          </section>
                        )}

                        {/* SEÇÃO 3 — Respostas */}
                        {lk.respostas && Object.keys(lk.respostas).length > 0 && (
                          <section>
                            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">3. Respostas do Diagnóstico</h3>
                            <div className="space-y-3">
                              {Object.entries(lk.respostas).map(([tela, resp]) => (
                                <div key={tela} className="bg-bege-fundo rounded-xl px-4 py-3">
                                  <div className="text-xs text-[#B08968] font-medium uppercase tracking-wide mb-1">{tela}</div>
                                  <div className="text-sm text-texto-escuro">{Array.isArray(resp) ? resp.join(', ') : String(resp)}</div>
                                </div>
                              ))}
                            </div>
                          </section>
                        )}

                        {/* SEÇÃO 4 — Script de vendas */}
                        <section>
                          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">4. Script de Vendas</h3>
                          <div className="space-y-4">
                            {[{ titulo: 'Mensagem 1 — Óleos indicados', texto: s1 }, { titulo: 'Mensagem 2 — Agendamento', texto: s2 }].map(({ titulo, texto }) => (
                              <div key={titulo}>
                                <div className="text-sm font-medium text-texto-suave mb-2">{titulo}</div>
                                <div className="bg-bege-fundo border border-bege-medio rounded-xl px-4 py-3 text-sm text-texto-escuro whitespace-pre-wrap mb-2">{texto}</div>
                                <button onClick={() => copiarK(texto)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D6A4F] text-white rounded-xl text-sm font-medium hover:bg-[#245A42] transition-all">
                                  <Copy className="w-4 h-4" /> Copiar mensagem
                                </button>
                              </div>
                            ))}
                          </div>
                        </section>

                        {/* SEÇÃO 5 — Óleos Touch (SEM valores) */}
                        {touch.length > 0 && (
                          <section>
                            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3 pb-2 border-b border-bege-medio">5. Óleos Touch Indicados</h3>
                            <div className="flex flex-wrap gap-2">
                              {touch.map(t => (
                                <span key={t} className="px-3 py-1.5 bg-verde-muito-claro text-verde-escuro rounded-full text-sm font-medium">{t}</span>
                              ))}
                            </div>
                          </section>
                        )}

                        {/* SEÇÃO 6 — Opções de compra (COM valores) */}
                        <section>
                          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">6. Opções de Compra</h3>
                          <div className="space-y-4">
                            {/* Opção 1 — Kit Kids Completo */}
                            <div className="border-2 border-[#2D6A4F] rounded-xl p-4 bg-[#F0FAF4]">
                              <div className="text-sm font-semibold text-[#2D6A4F] mb-2">Opção 1 — Kit Kids Completo</div>
                              <div className="text-xs font-medium text-[#2D6A4F] mb-2">🌿 Linha dōTERRA Kids — indicada para crianças e adolescentes de 0 a 17 anos</div>
              <div className="bg-[#D8F3DC] rounded-lg px-3 py-2 mb-3 text-xs text-[#2D6A4F] font-medium">
                                💰 Comprando o kit você economiza R$141,25 em relação à compra dos 7 óleos avulsos! Avulso: R$907,50 → Kit: R$766,25
                              </div>
                              <div className="flex justify-between flex-wrap gap-2">
                                <div>
                                  <div className="font-semibold text-[#2D6A4F] text-sm">🌿 Kit Kids Completo dōTERRA</div>
                                  <div className="text-xs text-texto-suave mt-0.5">7 mixes roll-on: Calmer, Thinker, Brave, Steady, Stronger, Tamer e Rescuer</div>
                                  <div className="text-xs text-[#B08968] mt-0.5">{KIT_COMPLETO_K.pv} PV — Subtotal: R${KIT_COMPLETO_K.preco.toFixed(2).replace('.', ',')} + frete R${FRETE_K.toFixed(2).replace('.', ',')}</div>
                                  {taxaCompleto === 0
                                    ? <div className="text-xs mt-1 font-semibold" style={{ color: '#2D6A4F' }}>🎉 Taxa de adesão isenta! 25% de desconto em futuras compras durante o ano.</div>
                                    : <div className="text-xs mt-1 font-semibold" style={{ color: '#B45309' }}>⚠️ Taxa de adesão: R$48,00 Inclui seleção de óleos e garante 25% de desconto durante o ano.</div>
                                  }
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-texto-escuro text-base">R${(totalCompleto + taxaCompleto).toFixed(2).replace('.', ',')}</div>
                                  <div className="text-xs text-texto-suave">{taxaCompleto > 0 ? 'c/ taxa + frete' : 'c/ frete'}</div>
                                  {parcCompleto.parcelas && <div className="text-xs text-[#2D6A4F] mt-0.5">{parcCompleto.parcelas.map(p => `${p.n}x R$${p.valor.toFixed(2).replace('.', ',')}`).join(' ou ')}</div>}
                                </div>
                              </div>
                              <button onClick={() => copiarK(gerarTextoOpcao(1, 'Kit Kids Completo', '7 mixes roll-on: Calmer, Thinker, Brave, Steady, Stronger, Tamer e Rescuer', totalCompleto, KIT_COMPLETO_K.pv, taxaCompleto, parcCompleto.parcelas))}
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#2D6A4F] text-white rounded-xl text-xs font-medium hover:bg-[#245A42] transition-all">
                                <Copy className="w-3.5 h-3.5" /> Copiar opção
                              </button>
                            </div>

                            {/* Opção 2 — Kit Prioritário Kids */}
                            <div className="border-2 border-bege-medio rounded-xl p-4">
                              <div className="text-sm font-semibold text-[#2C2C2A] mb-2">Opção 2 — Kit Prioritário Kids</div>
                              <div className="text-xs font-medium text-[#2D6A4F] mb-2">🌿 Linha dōTERRA Kids — indicada para crianças e adolescentes de 0 a 17 anos</div>
                              <div className="flex justify-between flex-wrap gap-2">
                                <div>
                                  <div className="font-semibold text-[#2C2C2A] text-sm">⭐ Kit Prioritário Kids</div>
                                  <div className="text-xs text-texto-suave mt-0.5">Mixes: {mixesTop3ComIngles || 'conforme diagnóstico'}</div>
                                  <div className="text-xs text-[#B08968] mt-0.5">{pvPrioritario} PV — Subtotal: R${totalPrioritarioProd.toFixed(2).replace('.', ',')} + frete R${FRETE_K.toFixed(2).replace('.', ',')}</div>
                                  {taxaPrioritario === 0
                                    ? <div className="text-xs mt-1 font-semibold" style={{ color: '#2D6A4F' }}>🎉 Taxa de adesão isenta! 25% de desconto em futuras compras durante o ano.</div>
                                    : <div className="text-xs mt-1 font-semibold" style={{ color: '#B45309' }}>⚠️ Taxa de adesão: R$48,00 Inclui seleção de óleos e garante 25% de desconto durante o ano.</div>
                                  }
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-texto-escuro text-base">R${(totalPrioritario + taxaPrioritario).toFixed(2).replace('.', ',')}</div>
                                  <div className="text-xs text-texto-suave">{taxaPrioritario > 0 ? 'c/ taxa + frete' : 'c/ frete'}</div>
                                  {parcPrioritario.parcelas && <div className="text-xs text-[#2D6A4F] mt-0.5">{parcPrioritario.parcelas.map(p => `${p.n}x R$${p.valor.toFixed(2).replace('.', ',')}`).join(' ou ')}</div>}
                                </div>
                              </div>
                              <button onClick={() => copiarK(gerarTextoOpcao(2, 'Kit Prioritário Kids', `Mixes: ${mixesTop3ComIngles || 'conforme diagnóstico'}`, totalPrioritario, pvPrioritario, taxaPrioritario, parcPrioritario.parcelas))}
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#2D6A4F] text-white rounded-xl text-xs font-medium hover:bg-[#245A42] transition-all">
                                <Copy className="w-3.5 h-3.5" /> Copiar opção
                              </button>
                            </div>

                            {/* Opção 3 — Kit Touch */}
                            {touch.length > 0 && (() => {
                              const totalTouchProd = touch.reduce((a, t) => a + (PRECOS_TOUCH_K[t]?.preco || 0), 0)
                              const totalTouch = totalTouchProd + FRETE_K
                              const pvTouch = touch.reduce((a, t) => a + (PRECOS_TOUCH_K[t]?.pv || 0), 0)
                              const taxaTouch = pvTouch >= PV_MIN_ISENCAO_K ? 0 : TAXA_ADESAO_K
                              const parcTouch = calcParcK(totalTouch + taxaTouch)
                              return (
                                <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                                  <div className="text-sm font-semibold text-blue-800 mb-2">Opção 3 — Kit Touch</div>
                                  <div className="text-xs font-medium text-blue-700 mb-2">💡 Óleos touch são diluídos e prontos para usar. Também podem ser usados por toda a família.</div>
                                  <div className="flex justify-between flex-wrap gap-2">
                                    <div>
                                      <div className="font-semibold text-blue-800 text-sm">💧 Kit Touch</div>
                                      <div className="text-xs text-texto-suave mt-0.5">{touch.join(', ')}</div>
                                      <div className="text-xs text-[#B08968] mt-0.5">{pvTouch} PV — Subtotal: R${totalTouchProd.toFixed(2).replace('.', ',')} + frete R${FRETE_K.toFixed(2).replace('.', ',')}</div>
                                      {taxaTouch === 0
                                        ? <div className="text-xs mt-1 font-semibold" style={{ color: '#2D6A4F' }}>🎉 Taxa de adesão isenta! 25% de desconto em futuras compras durante o ano.</div>
                                        : <div className="text-xs mt-1 font-semibold" style={{ color: '#B45309' }}>⚠️ Taxa de adesão: R$48,00 Inclui seleção de óleos e garante 25% de desconto durante o ano.</div>
                                      }
                                    </div>
                                    <div className="text-right">
                                      <div className="font-bold text-texto-escuro text-base">R${(totalTouch + taxaTouch).toFixed(2).replace('.', ',')}</div>
                                      <div className="text-xs text-texto-suave">{taxaTouch > 0 ? 'c/ taxa + frete' : 'c/ frete'}</div>
                                      {parcTouch.parcelas && <div className="text-xs text-[#2D6A4F] mt-0.5">{parcTouch.parcelas.map(p => `${p.n}x R$${p.valor.toFixed(2).replace('.', ',')}`).join(' ou ')}</div>}
                                    </div>
                                  </div>
                                  <button onClick={() => copiarK(gerarTextoOpcao(3, 'Kit Touch', touch.join(', '), totalTouch, pvTouch, taxaTouch, parcTouch.parcelas))}
                                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-700 text-white rounded-xl text-xs font-medium hover:bg-blue-800 transition-all">
                                    <Copy className="w-3.5 h-3.5" /> Copiar opção
                                  </button>
                                </div>
                              )
                            })()}

                            {/* Opção 4 — Kit Premium */}
                            <div className="border-2 border-[#B08968] rounded-xl p-4">
                              <div className="text-sm font-semibold text-[#B08968] mb-2">Opção 4 — Kit Premium</div>
                              <div className="text-xs font-medium text-[#2D6A4F] mb-2">🌿 Linha dōTERRA Kids — indicada para crianças e adolescentes de 0 a 17 anos</div>
                              <div className="flex justify-between flex-wrap gap-2">
                                <div>
                                  <div className="font-semibold text-[#B08968] text-sm">👑 Kit Premium</div>
                                  <div className="text-xs text-texto-suave mt-0.5">Kit Kids completo (7 mixes) + Difusor Petal 2.0</div>
                                  <div className="text-xs text-[#2D6A4F] mt-0.5 font-medium">🎁 Brindes: Lavanda 5ml + Laranja-selvagem 5ml incluídos!</div>
                                  <div className="text-xs text-[#B08968] mt-0.5">{pvPremium} PV — Subtotal: R${totalPremiumProd.toFixed(2).replace('.', ',')} + frete R${FRETE_K.toFixed(2).replace('.', ',')}</div>
                                  {taxaPremium === 0
                                    ? <div className="text-xs mt-1 font-semibold" style={{ color: '#2D6A4F' }}>🎉 Taxa de adesão isenta! 25% de desconto em futuras compras durante o ano.</div>
                                    : <div className="text-xs mt-1 font-semibold" style={{ color: '#B45309' }}>⚠️ Taxa de adesão: R$48,00 Inclui seleção de óleos e garante 25% de desconto durante o ano.</div>
                                  }
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-texto-escuro text-base">R${(totalPremium + taxaPremium).toFixed(2).replace('.', ',')}</div>
                                  <div className="text-xs text-texto-suave">{taxaPremium > 0 ? 'c/ taxa + frete' : 'c/ frete'}</div>
                                  {parcPremium.parcelas && <div className="text-xs text-[#2D6A4F] mt-0.5">{parcPremium.parcelas.map(p => `${p.n}x R$${p.valor.toFixed(2).replace('.', ',')}`).join(' ou ')}</div>}
                                </div>
                              </div>
                              <button onClick={() => copiarK(gerarTextoOpcao(4, 'Kit Premium', 'Kit Kids Completo (7 mixes) + Difusor Petal 2.0 + Brindes: Lavanda 5ml + Laranja-selvagem 5ml', totalPremium, pvPremium, taxaPremium, parcPremium.parcelas))}
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#B08968] text-white rounded-xl text-xs font-medium hover:bg-[#8C6D4F] transition-all">
                                <Copy className="w-3.5 h-3.5" /> Copiar opção
                              </button>
                            </div>
                          </div>
                        </section>

                        {/* SEÇÃO 7 — Protocolo de Uso (pós-venda) */}
                        <section>
                          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-bege-medio">7. 🌿 Protocolo de Uso</h3>
                          <p className="text-xs text-texto-suave mb-4 italic">Recurso de pós-venda: envie para a mãe após a compra para orientar como usar cada óleo.</p>
                          <BlocoProtocoloUso
                            faixaEtaria={lk.faixaEtaria || ''}
                            nomeCrianca={nomeKidD}
                            mixesIndicados={mixes}
                            oleosTouchInd={touch}
                          />
                        </section>

                        {/* Orientação de abordagem já foi movida para posição 2 */}

                        {/* Status e ações */}
                        <div className="flex flex-wrap gap-3 pt-4 border-t border-bege-medio">
                          <div className="relative flex-1 min-w-[200px]">
                            <select
                              value={lk.status || 'Novo lead'}
                              onChange={e => atualizarStatusKid(lk._id, e.target.value)}
                              className="w-full appearance-none px-4 py-3 pr-10 rounded-xl text-sm font-medium cursor-pointer focus:outline-none"
                              style={{ backgroundColor: obterCoresStatusK(lk.status).bg, color: obterCoresStatusK(lk.status).text }}
                            >
                              <option value="Novo lead">Novo contato</option>
                              <option value="Em contato">Em contato</option>
                              <option value="Agendado">Agendado</option>
                              <option value="Convertido">Convertido</option>
                              <option value="Não respondeu">Não respondeu</option>
                              <option value="Não qualificado">Não qualificado</option>
                            </select>
                          </div>
                          {/* ações contato infantil */}
                          <a href={`https://wa.me/${(lk.whatsapp || '').replace(/\D/g, '')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#1ea34e] transition-all">
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                          </a>
                          <button onClick={() => excluirLeadKid(lk._id)}
                            className="px-5 py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 flex items-center gap-2">
                            <Trash2 className="w-4 h-4" /> Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </>
          )
        })()}

        {/* ═══════════════════════════════════ */}
        {/* ABA: COMO DIVULGAR SEU LINK        */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'divulgar' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1">
                Como Divulgar seu Link
              </h2>
              <p className="text-sm text-texto-suave">
                Modelos de mensagem prontos para copiar e estratégias para cada canal.
              </p>
            </div>
            <DivulgarLink
              usuarioConsultora={
                perfilLogado?.tipo === 'consultora'
                  ? perfilLogado.usuario
                  : perfilLogado?.tipo === 'admin'
                  ? 'monica'
                  : ''
              }
              nomeConsultora={
                perfilLogado?.tipo === 'consultora'
                  ? (consultoraLogadaObj?.nome ?? perfilLogado.nome)
                  : 'Mônica'
              }
              acessoCapacitacao={true}
            />
          </>
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: CONSULTORAS                   */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'consultoras' && isAdmin && (() => {
          const agora = new Date()
          const inicioMes = new Date(agora.getFullYear(), agora.getMonth(), 1)
          const inicioMesAnterior = new Date(agora.getFullYear(), agora.getMonth() - 1, 1)
          const consultorasNaoCapacitacao = consultoras.filter(c => c.tipoAcesso !== 'capacitacao')
          const totalAtivas = consultorasNaoCapacitacao.filter(c => c.ativo).length
          const totalMensais = consultorasNaoCapacitacao.filter(c => c.tipoAcesso === 'mensal').length
          const totalAnuais = consultorasNaoCapacitacao.filter(c => c.tipoAcesso === 'anual').length
          const diagnosticosAdultoMes = leads.filter(l => l.data_hora && new Date(l.data_hora) >= inicioMes).length
          const diagnosticosAdultoMesAnterior = leads.filter(l => l.data_hora && new Date(l.data_hora) >= inicioMesAnterior && new Date(l.data_hora) < inicioMes).length
          const diagnosticosKidsMes = leadsKids.filter(l => l.data_hora && new Date(l.data_hora) >= inicioMes).length
          const diagnosticosKidsMesAnterior = leadsKids.filter(l => l.data_hora && new Date(l.data_hora) >= inicioMesAnterior && new Date(l.data_hora) < inicioMes).length
          const totalMesAtual = diagnosticosAdultoMes + diagnosticosKidsMes
          const totalMesAnterior = diagnosticosAdultoMesAnterior + diagnosticosKidsMesAnterior
          const crescimentoMensal = totalMesAnterior > 0
            ? Math.round(((totalMesAtual - totalMesAnterior) / totalMesAnterior) * 100)
            : (totalMesAtual > 0 ? 100 : 0)

          const detalheEmpresasCount = consultoraDetalhe ? empresasList.filter(e => e.consultora === consultoraDetalhe.usuario).length : 0
          const detalheLeadsAdulto = consultoraDetalhe ? leads.filter(l => l.consultora === consultoraDetalhe.usuario) : []
          const detalheLeadsKids = consultoraDetalhe ? leadsKids.filter(l => l.consultora === consultoraDetalhe.usuario) : []
          const detalheUltimoDiagnostico = [...detalheLeadsAdulto, ...detalheLeadsKids]
            .map(l => l.data_hora)
            .filter(Boolean)
            .sort()
            .pop()

          return (
          <>
            {/* Resumo geral do sistema */}
            <div className="mb-8">
              <h2 className="text-xl font-cormorant font-semibold text-texto-escuro mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-verde-escuro" />
                Visão Geral do Sistema
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Consultoras ativas', valor: totalAtivas, cor: 'text-verde-escuro' },
                  { label: 'Consultoras mensais', valor: totalMensais, cor: 'text-blue-600' },
                  { label: 'Consultoras anuais', valor: totalAnuais, cor: 'text-[#B08968]' },
                  { label: 'Diagnósticos adulto (mês)', valor: diagnosticosAdultoMes, cor: 'text-texto-escuro' },
                  { label: 'Diagnósticos kids (mês)', valor: diagnosticosKidsMes, cor: 'text-pink-600' },
                  { label: 'Crescimento mensal', valor: `${crescimentoMensal >= 0 ? '+' : ''}${crescimentoMensal}%`, cor: crescimentoMensal >= 0 ? 'text-green-600' : 'text-red-600' },
                  { label: 'Vivências iniciadas', valor: '—', cor: 'text-texto-suave' },
                  { label: 'Vivências concluídas', valor: '—', cor: 'text-texto-suave' },
                ].map(m => (
                  <div key={m.label} className="bg-white p-5 rounded-xl shadow-md">
                    <div className={`text-2xl font-bold mb-1 ${m.cor}`}>{m.valor}</div>
                    <div className="text-xs text-texto-suave">{m.label}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-texto-suave mt-2 italic">
                Métricas de vivências serão exibidas quando o módulo de vivências estiver disponível.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
              <h2 className="text-xl font-cormorant font-semibold text-texto-escuro mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-verde-escuro" />
                Cadastrar novo(a) consultor(a) (rápido)
              </h2>
              <p className="text-sm text-texto-suave mb-4">
                Cadastro rápido cria o(a) consultor(a) como <strong>gratuito(a)</strong> (sem expiração). Para acessos mensal ou anual, use a aba "Gestão de Acessos".
              </p>
              <form onSubmit={cadastrarConsultora} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Nome completo</label>
                  <input
                    type="text"
                    value={novaConsultoraNome}
                    onChange={e => setNovaConsultoraNome(e.target.value)}
                    placeholder="Ex: Ana Silva"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Usuário (slug)</label>
                  <input
                    type="text"
                    value={novaConsultoraUsuario}
                    onChange={e => setNovaConsultoraUsuario(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                    placeholder="Ex: ana"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">E-mail *</label>
                  <input
                    type="email"
                    value={novaConsultoraEmail}
                    onChange={e => setNovaConsultoraEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                  <p className="text-xs text-texto-suave mt-1">A senha provisória é gerada automaticamente e enviada por e-mail.</p>
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2 rounded-xl hover:bg-opacity-90 transition-all font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-bege-medio">
                <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                  <Users className="w-5 h-5 text-verde-escuro" />
                  Consultores cadastrados ({consultoras.filter(c => c.tipoAcesso !== 'capacitacao').length})
                </h2>
              </div>

              {loadingConsultoras ? (
                <div className="p-8 text-center text-texto-suave">Carregando...</div>
              ) : consultoras.filter(c => c.tipoAcesso !== 'capacitacao').length === 0 ? (
                <div className="p-8 text-center text-texto-suave">Nenhum consultor cadastrado ainda.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bege-fundo">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Usuário</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Link personalizado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Contatos</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Plano</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Assinatura</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bege-medio">
                      {consultoras.filter(c => c.tipoAcesso !== 'capacitacao').map(consultora => {
                        const coresAssin = coresStatusAssinatura(consultora.statusAssinatura)
                        return (
                          <tr key={consultora._id} className="hover:bg-bege-fundo">
                            <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{consultora.nome}</td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{consultora.usuario}</td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{consultora.email || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-texto-suave text-xs truncate max-w-[220px]">
                                  www.descubraseusoleos.com.br/?c={consultora.usuario}
                                </span>
                                <button
                                  onClick={() => copiarLinkConsultora(consultora.usuario)}
                                  className="p-1.5 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors flex-shrink-0"
                                  title="Copiar link"
                                >
                                  <LinkIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                {leadsCountPorConsultora[consultora.usuario] || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${consultora.planoAssinatura ? 'bg-[#D8F3DC] text-[#2D6A4F]' : 'bg-[#F3F4F6] text-[#4B5563]'}`}
                              >
                                {consultora.planoAssinatura ? 'Assinatura ativa' : 'Sem assinatura'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className="px-2 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: coresAssin.bg, color: coresAssin.text }}
                              >
                                {rotuloStatus(consultora.statusAssinatura)}
                              </span>
                              {consultora.dataExpiracao ? (
                                <div className="text-xs text-texto-suave mt-1">
                                  Expira: {formatarData(consultora.dataExpiracao)}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${consultora.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                              >
                                {consultora.ativo ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="flex flex-row items-center gap-2">
                                <button
                                  onClick={() => setConsultoraDetalhe(consultora)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Ver detalhes"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setAbaAtiva('acessos'); iniciarEdicaoAcesso(consultora) }}
                                  className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors"
                                  title="Editar acesso"
                                >
                                  <Crown className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => desativarReativarConsultora(consultora)}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    consultora.statusAssinatura === 'inativo'
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                  }`}
                                  title={consultora.statusAssinatura === 'inativo' ? 'Reativar conta' : 'Desativar conta'}
                                >
                                  {consultora.statusAssinatura === 'inativo' ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                                  {consultora.statusAssinatura === 'inativo' ? 'Reativar' : 'Desativar'}
                                </button>
                                <button
                                  onClick={() => setConfirmarExclusao(consultora)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                  title="Excluir conta permanentemente"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal de detalhe por consultora */}
            {consultoraDetalhe && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
                  <div className="sticky top-0 bg-[#2D6A4F] text-white px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
                    <div>
                      <h2 className="text-xl font-cormorant font-semibold">{consultoraDetalhe.nome}</h2>
                      <p className="text-[#A8DFC4] text-xs mt-0.5">@{consultoraDetalhe.usuario}</p>
                    </div>
                    <button onClick={() => setConsultoraDetalhe(null)} className="text-white hover:text-gray-200">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <section>
                      <h3 className="text-base font-semibold text-[#2D6A4F] mb-3 pb-1 border-b border-bege-medio">Dados cadastrais</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: 'Nome', valor: consultoraDetalhe.nome },
                          { label: 'E-mail', valor: consultoraDetalhe.email },
                          { label: 'Assinatura', valor: consultoraDetalhe.planoAssinatura ? 'Ativa' : 'Sem assinatura' },
                          { label: 'Periodicidade', valor: consultoraDetalhe.tipoAcesso },
                          { label: 'Status da assinatura', valor: rotuloStatus(consultoraDetalhe.statusAssinatura) },
                          { label: 'Data de cadastro', valor: consultoraDetalhe.createdAt ? new Date(consultoraDetalhe.createdAt).toLocaleDateString('pt-BR') : '—' },
                          { label: 'Data da última atividade', valor: consultoraDetalhe.updatedAt ? new Date(consultoraDetalhe.updatedAt).toLocaleDateString('pt-BR') : '—' },
                        ].map(({ label, valor }) => (
                          <div key={label}>
                            <div className="text-xs text-texto-suave mb-0.5 font-medium">{label}</div>
                            <div className="text-sm text-texto-escuro">{valor || '—'}</div>
                          </div>
                        ))}
                      </div>
                    </section>
                    <section>
                      <h3 className="text-base font-semibold text-[#2D6A4F] mb-3 pb-1 border-b border-bege-medio">Atividade no sistema</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: 'Diagnósticos adulto', valor: detalheLeadsAdulto.length },
                          { label: 'Diagnósticos kids', valor: detalheLeadsKids.length },
                          { label: 'Total de contatos', valor: detalheLeadsAdulto.length + detalheLeadsKids.length },
                          { label: 'Empresas cadastradas', valor: detalheEmpresasCount },
                          { label: 'Vivências iniciadas', valor: '—' },
                          { label: 'Vivências concluídas', valor: '—' },
                          { label: 'Último diagnóstico', valor: detalheUltimoDiagnostico ? new Date(detalheUltimoDiagnostico).toLocaleDateString('pt-BR') : '—' },
                        ].map(({ label, valor }) => (
                          <div key={label}>
                            <div className="text-xs text-texto-suave mb-0.5 font-medium">{label}</div>
                            <div className="text-sm text-texto-escuro">{valor}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-texto-suave mt-2 italic">
                        Métricas de vivências serão exibidas quando o módulo de vivências estiver disponível. Respostas individuais dos diagnósticos, dados pessoais dos contatos e histórico de atendimento não são exibidos aqui, por privacidade.
                      </p>
                    </section>
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-bege-medio">
                      <button
                        onClick={() => { setAbaAtiva('acessos'); iniciarEdicaoAcesso(consultoraDetalhe); setConsultoraDetalhe(null) }}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#D8F3DC] text-[#2D6A4F] hover:bg-[#52B788] hover:text-white rounded-xl text-sm font-medium transition-all"
                      >
                        <Crown className="w-4 h-4" /> Editar acesso
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
          )
        })()}

        {/* ═══════════════════════════════════ */}
        {/* ABA: GESTAO DE ACESSOS             */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'acessos' && isAdmin && (
          <>
            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                  <Crown className="w-5 h-5 text-verde-escuro" />
                  {editandoConsultora ? `Editando acesso: ${editandoConsultora.nome}` : 'Cadastrar acesso manualmente'}
                </h2>
                {editandoConsultora && (
                  <button
                    onClick={limparFormAcesso}
                    className="text-sm text-texto-suave hover:text-texto-escuro underline"
                  >
                    Cancelar edição
                  </button>
                )}
              </div>
              <form onSubmit={salvarAcesso} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Nome completo *</label>
                  <input
                    type="text"
                    value={acessoNome}
                    onChange={e => setAcessoNome(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">E-mail</label>
                  <input
                    type="email"
                    value={acessoEmail}
                    onChange={e => setAcessoEmail(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">WhatsApp</label>
                  <input
                    type="text"
                    value={acessoWhatsapp}
                    onChange={e => setAcessoWhatsapp(e.target.value)}
                    placeholder="+55 11 99999-9999"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Usuário (slug) *</label>
                  <input
                    type="text"
                    value={acessoUsuario}
                    onChange={e => setAcessoUsuario(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                {!editandoConsultora && (
                  <div className="md:col-span-2 lg:col-span-3 bg-[#FFFBEB] border border-[#F59E0B] rounded-xl px-4 py-3 text-sm text-[#92400E]">
                    A senha provisória é gerada automaticamente e enviada para o e-mail informado. Ela não aparece neste painel.
                  </div>
                )}
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Tipo de acesso</label>
                  <select
                    value={acessoTipo}
                    onChange={e => mudarTipoAcesso(e.target.value as TipoAcesso)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  >
                    <option value="gratuito">Gratuito (sem expiração)</option>
                    <option value="mensal">Mensal (30 dias)</option>
                    <option value="anual">Anual (365 dias)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Registro de assinatura</label>
                  <select
                    value={acessoPlano}
                    onChange={e => setAcessoPlano(e.target.value as PlanoAssinatura)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  >
                    <option value="essencial">Assinatura registrada</option>
                    <option value="completo">Assinatura registrada (histórico)</option>
                  </select>
                  <p className="text-xs text-texto-suave mt-1">Registro interno do banco. O acesso é o mesmo nos dois casos.</p>
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">
                    Data de expiração
                    {acessoTipo === 'gratuito' && <span className="text-xs text-texto-suave ml-2">(não aplicável)</span>}
                  </label>
                  <input
                    type="date"
                    value={acessoDataExpiracao}
                    onChange={e => setAcessoDataExpiracao(e.target.value)}
                    disabled={acessoTipo === 'gratuito'}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Status</label>
                  <select
                    value={acessoStatus}
                    onChange={e => setAcessoStatus(e.target.value as StatusAssinatura)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="gratuito">Gratuito</option>
                    <option value="expirado">Expirado</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2 rounded-xl hover:bg-opacity-90 transition-all font-medium"
                  >
                    {editandoConsultora ? 'Salvar alterações' : 'Cadastrar acesso'}
                  </button>
                </div>
              </form>
            </div>

            {/* Tabela de acessos */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-bege-medio">
                <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                  <Users className="w-5 h-5 text-verde-escuro" />
                  Todos os acessos ({consultoras.length})
                </h2>
              </div>
              {consultoras.length === 0 ? (
                <div className="p-8 text-center text-texto-suave">Nenhum acesso cadastrado.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bege-fundo">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail / WhatsApp</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Tipo</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Plano</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Expira em</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Dias restantes</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Assinatura</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Distribuidora</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Origem</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Data de cadastro</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bege-medio">
                      {consultoras.map(c => {
                        const dias = diasRestantes(c.dataExpiracao)
                        const coresAssin = coresStatusAssinatura(c.statusAssinatura)
                        const origem = origemConsultora(c)
                        const coresOrigem = coresOrigemConsultora(origem)
                        return (
                          <tr key={c._id} className="hover:bg-bege-fundo">
                            <td className="px-4 py-3 text-sm text-texto-escuro font-medium">
                              {c.nome}
                              <div className="text-xs text-texto-suave">@{c.usuario}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-suave">
                              {c.email || '—'}
                              <div className="text-xs">{c.whatsapp || ''}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-suave capitalize">{c.tipoAcesso || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              {c.tipoAcesso === 'capacitacao' ? (
                                <span className="text-texto-suave text-xs">—</span>
                              ) : (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.planoAssinatura ? 'bg-[#D8F3DC] text-[#2D6A4F]' : 'bg-[#F3F4F6] text-[#4B5563]'}`}>
                                  {c.planoAssinatura ? 'Assinatura ativa' : 'Sem assinatura'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{c.tipoAcesso === 'capacitacao' ? '∞' : formatarData(c.dataExpiracao)}</td>
                            <td className="px-4 py-3 text-sm">
                              {c.tipoAcesso === 'capacitacao' ? (
                                <span className="text-texto-escuro">∞</span>
                              ) : dias === null ? '∞' : dias < 0 ? (
                                <span className="text-red-600 font-medium">expirou</span>
                              ) : (
                                <span className={dias <= 2 ? 'text-yellow-700 font-medium' : 'text-texto-escuro'}>{dias} dia(s)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className="px-2 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: coresAssin.bg, color: coresAssin.text }}
                              >
                                {rotuloStatus(c.statusAssinatura)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{c.distribuidora || '—'}</td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="flex flex-row items-center gap-2">
                                <button
                                  onClick={() => iniciarEdicaoAcesso(c)}
                                  className="px-3 py-1.5 text-sm text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => desativarReativarConsultora(c)}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    c.statusAssinatura === 'inativo'
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                  }`}
                                  title={c.statusAssinatura === 'inativo' ? 'Reativar conta' : 'Desativar conta'}
                                >
                                  {c.statusAssinatura === 'inativo' ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                                  {c.statusAssinatura === 'inativo' ? 'Reativar' : 'Desativar'}
                                </button>
                                <button
                                  onClick={() => setConfirmarExclusao(c)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                  title="Excluir conta permanentemente"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: DISTRIBUIDORAS                */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'distribuidoras' && isAdmin && (
          <>
            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
              <h2 className="text-xl font-cormorant font-semibold text-texto-escuro mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-verde-escuro" />
                Cadastrar nova distribuidora
              </h2>
              <form onSubmit={cadastrarDistribuidora} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Nome *</label>
                  <input
                    type="text"
                    value={novaDistNome}
                    onChange={e => setNovaDistNome(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Slug (identificador) *</label>
                  <input
                    type="text"
                    value={novaDistSlug}
                    onChange={e => setNovaDistSlug(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                    placeholder="ex: mariasouza"
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Senha *</label>
                  <input
                    type="text"
                    value={novaDistSenha}
                    onChange={e => setNovaDistSenha(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">E-mail</label>
                  <input
                    type="email"
                    value={novaDistEmail}
                    onChange={e => setNovaDistEmail(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">WhatsApp</label>
                  <input
                    type="text"
                    value={novaDistWhats}
                    onChange={e => setNovaDistWhats(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-texto-suave mb-2">Total de acessos *</label>
                  <input
                    type="number"
                    min="1"
                    value={novaDistTotal}
                    onChange={e => setNovaDistTotal(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                  />
                </div>
                <div className="md:col-span-3 flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-verde-escuro text-white px-6 py-2 rounded-xl hover:bg-opacity-90 transition-all font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar distribuidora
                  </button>
                </div>
              </form>
            </div>

            {loadingDistribuidoras ? (
              <div className="bg-white p-8 rounded-xl shadow-md text-center text-texto-suave">Carregando...</div>
            ) : distribuidoras.length === 0 ? (
              <div className="bg-white p-8 rounded-xl shadow-md text-center text-texto-suave">
                Nenhuma distribuidora cadastrada ainda.
              </div>
            ) : (
              <div className="space-y-6">
                {distribuidoras.map(d => {
                  const vinculadas = consultorasPorDistribuidora[d.slug] || []
                  const utilizados = vinculadas.length
                  const disponiveis = Math.max(0, d.totalAcessos - utilizados)
                  return (
                    <div key={d._id} className="bg-white rounded-xl shadow-md overflow-hidden">
                      <div className="px-6 py-4 border-b border-bege-medio flex flex-col md:flex-row md:items-center gap-3 justify-between">
                        <div>
                          <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">
                            {d.nome} <span className="text-sm text-texto-suave">@{d.slug}</span>
                          </h3>
                          <div className="text-sm text-texto-suave">
                            {d.email || '—'} {d.whatsapp ? `· ${d.whatsapp}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copiarLinkDist(d.slug)}
                            className="flex items-center gap-1.5 text-sm text-verde-escuro hover:bg-verde-muito-claro px-3 py-1.5 rounded-lg transition-colors"
                            title="Copiar link de cadastro"
                          >
                            <LinkIcon className="w-4 h-4" />
                            Copiar link
                          </button>
                          <button
                            onClick={() => toggleAtivoDist(d)}
                            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors ${d.ativo ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                          >
                            {d.ativo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            {d.ativo ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            onClick={() => excluirDist(d._id)}
                            className="flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Excluir
                          </button>
                        </div>
                      </div>

                      {/* Métricas */}
                      <div className="grid grid-cols-3 gap-4 p-6">
                        <div className="bg-bege-fundo p-4 rounded-xl text-center">
                          <div className="text-2xl font-bold text-verde-escuro">{d.totalAcessos}</div>
                          <div className="text-xs text-texto-suave uppercase tracking-wide">Total do pacote</div>
                        </div>
                        <div className="bg-bege-fundo p-4 rounded-xl text-center">
                          <div className="text-2xl font-bold text-yellow-600">{utilizados}</div>
                          <div className="text-xs text-texto-suave uppercase tracking-wide">Utilizados</div>
                        </div>
                        <div className="bg-bege-fundo p-4 rounded-xl text-center">
                          <div className="text-2xl font-bold text-green-600">{disponiveis}</div>
                          <div className="text-xs text-texto-suave uppercase tracking-wide">Disponíveis</div>
                        </div>
                      </div>

                      {/* Consultoras vinculadas */}
                      <div className="px-6 pb-6">
                        <h4 className="text-sm font-medium text-texto-escuro mb-2">
                          Consultores vinculados ({vinculadas.length})
                        </h4>
                        {vinculadas.length === 0 ? (
                          <div className="text-sm text-texto-suave py-3">
                            Nenhum consultor cadastrado pelo link desta distribuidora ainda.
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-bege-fundo">
                                <tr>
                                  <th className="px-3 py-2 text-left">Nome</th>
                                  <th className="px-3 py-2 text-left">E-mail</th>
                                  <th className="px-3 py-2 text-left">Assinatura</th>
                                  <th className="px-3 py-2 text-left">Expira</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-bege-medio">
                                {vinculadas.map(c => {
                                  const coresAssin = coresStatusAssinatura(c.statusAssinatura)
                                  return (
                                    <tr key={c._id}>
                                      <td className="px-3 py-2 text-texto-escuro">{c.nome}</td>
                                      <td className="px-3 py-2 text-texto-suave">{c.email || '—'}</td>
                                      <td className="px-3 py-2">
                                        <span
                                          className="px-2 py-0.5 rounded-full text-xs font-medium"
                                          style={{ backgroundColor: coresAssin.bg, color: coresAssin.text }}
                                        >
                                          {rotuloStatus(c.statusAssinatura)}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-texto-suave">{formatarData(c.dataExpiracao)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
        {abaAtiva === 'calculadora' && (
          <AbaCalculadoraOleosEssenciais />
        )}

        {abaAtiva === 'protocolos' && (
          <AbaProtocolosOleosEssenciais />
        )}

        {abaAtiva === 'perfil' && !isAdmin && perfilLogado?.tipo === 'consultora' && (
          <MeuPerfilAba
            usuarioLogado={perfilLogado.usuario}
            nomeLogado={perfilLogado.nome}
          />
        )}



        {/* ═══════════════════════════════════ */}
        {/* ABA: CONFIGURAÇÕES                 */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'configuracoes' && isAdmin && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
                <Settings className="w-6 h-6 text-verde-escuro" />
                Configurações
              </h2>
              <p className="text-sm text-texto-suave">
                Ajuste links e parâmetros que aparecem para todos os consultores.
              </p>
            </div>
            <LinkMateriaisConfig />
          </>
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: COMO USAR O SISTEMA (TREINAMENTO) */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'treinamento' && (
          <AbaTreinamento
            isAdmin={isAdmin}
            consultoraLogada={consultoraLogada}
            consultoras={consultoras}
            onRecarregarConsultoras={carregarConsultoras}
          />
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: EMPRESAS (CORPORATIVO)         */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'empresas' && (
          <AbaEmpresas
            consultoraAdmin={isAdmin ? 'monica' : (consultoraLogada ?? '')}
            isAdmin={isAdmin}
            tituloProfissional={(() => {
              if (isAdmin) {
                const monica = consultoras.find(c => c.usuario === 'monica')
                return (monica as any)?.tituloProfissional || ''
              }
              const consultora = consultoras.find(c => c.usuario === consultoraLogada)
              return (consultora as any)?.tituloProfissional || ''
            })()}
            podeAcessar={isAdmin ? true : temAcessoEmpresas(consultoraLogadaObj)}
          />
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: INSCRITOS NA CAPACITAÇÃO       */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'inscritos' && (
          <AbaCursosAromaterapia
            isAdmin={isAdmin}
            usuarioConsultora={
              perfilLogado?.tipo === 'consultora'
                ? perfilLogado.usuario
                : 'monica'
            }
            nomeConsultora={
              perfilLogado?.tipo === 'consultora'
                ? (consultoraLogadaObj?.nome ?? perfilLogado.nome)
                : 'Mônica'
            }
            filtrarPorConsultora={isAdmin ? null : consultoraLogada}
          />
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: VIVÊNCIAS TERAPÊUTICAS         */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'vivencias' && (
          <AbaVivencias
            consultoraAdmin={isAdmin ? 'monica' : (consultoraLogada ?? '')}
            isAdmin={isAdmin}
            podeAcessar={true}
          />
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: COMO CRESCER                   */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'como_crescer' && (
          <AbaComoCrescer
            consultoraAdmin={isAdmin ? 'monica' : (consultoraLogada ?? '')}
            isAdmin={isAdmin}
          />
        )}

        {/* ═══════════════════════════════════ */}
        {/* ABA: MENTORIA — apenas administradora */}
        {/* ═══════════════════════════════════ */}
        {abaAtiva === 'mentoria' && isAdmin && (
          <AbaMentoria
            consultoraAdmin="monica"
            isAdmin={isAdmin}
          />
        )}
      </div>

      {/* ═══════════════════════════════════════ */}
      {/* MODAL DE DETALHES DO LEAD              */}
      {/* ═══════════════════════════════════════ */}
      {/* Modal de confirmacao de exclusao de consultora */}
      {confirmarExclusao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">
                  Excluir conta de {confirmarExclusao.nome}?
                </h3>
              </div>
            </div>
            <p className="text-texto-suave text-sm mb-6 leading-relaxed">
              Essa ação é irreversível e todos os dados serão perdidos permanentemente, incluindo leads e histórico.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmarExclusao(null)}
                className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => excluirConsultora(confirmarExclusao._id)}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════ */}
      {/* BOTÃO FLUTUANTE DE SUPORTE               */}
      {/* ═══════════════════════════════════════ */}
      {(() => {
        const nomeConsultoraLogada =
          perfilLogado?.tipo === 'consultora'
            ? (perfilLogado.nome || '').trim().split(/\s+/)[0]
            : 'Administradora'
        const mensagemSuporte = encodeURIComponent(
          `Olá Mônica! Sou ${nomeConsultoraLogada} usuária do Sistema Descubra seus Óleos e preciso de ajuda com: `
        )
        return (
          <a
            href={`https://wa.me/5581988671210?text=${mensagemSuporte}`}
            target="_blank"
            rel="noopener noreferrer"
            className="fixed z-40 flex items-center gap-2 bg-[#2D6A4F] text-white font-medium rounded-full shadow-lg hover:bg-[#245A42] transition-colors select-none"
            style={{
              bottom: 24,
              right: 24,
              height: 48,
              paddingLeft: 18,
              paddingRight: 20,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              animation: 'suporte-pulse 5s ease-in-out infinite',
            }}
            title="Falar com o suporte"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
              style={{ width: 20, height: 20, flexShrink: 0 }}>
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
            <span style={{ fontSize: 14 }}>Suporte</span>
          </a>
        )
      })()}

      <style>{`
        @keyframes suporte-pulse {
          0%, 100% { transform: scale(1); }
          90% { transform: scale(1); }
          93% { transform: scale(1.05); }
          96% { transform: scale(1); }
          98% { transform: scale(1.04); }
        }
      `}</style>

      {modalAberto && leadSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-verde-escuro text-white px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-cormorant font-semibold">Detalhes do contato</h2>
              <button
                onClick={() => setModalAberto(false)}
                className="text-white hover:text-gray-200 text-2xl"
              >
                x
              </button>
            </div>

            <div className="p-6">
              <div className="mb-8">
                <div
                  className="flex items-start gap-3 p-4 rounded-xl mb-6"
                  style={{ backgroundColor: '#FEE2E2', border: '1px solid #F87171' }}
                >
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#991B1B' }}>
                    {`Essas são as respostas após o preenchimento do diagnóstico. Confira com atenção para entender as demandas da pessoa com quem você vai conversar no WhatsApp. Leia tudo antes de entrar em contato. Observe especialmente o momento dela, o principal obstáculo e as prioridades selecionadas.`}
                  </div>
                </div>
                <h3 className="text-2xl font-cormorant font-semibold text-verde-escuro mb-6 pb-3 border-b-2 border-verde-escuro">
                  Respostas do Diagnóstico
                </h3>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Nome</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.nome}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">WhatsApp</div>
                      <div className="text-texto-escuro text-lg">{exibirWhatsAppFormatado(leadSelecionado.whatsapp, leadSelecionado.pais)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Idade</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.idade ? `${leadSelecionado.idade} anos` : 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">E-mail</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.email || 'Não informado'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">País</div>
                      <div className="text-texto-escuro text-lg">
                        {leadSelecionado.pais === 'Portugal' ? '🇵🇹 Portugal' : leadSelecionado.pais === 'Estados Unidos' ? '🇺🇸 Estados Unidos' : leadSelecionado.pais === 'Outro' ? '🌍 Outro' : '🇧🇷 Brasil'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Data de preenchimento</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.data_hora ? new Date(leadSelecionado.data_hora).toLocaleString('pt-BR') : 'Não informado'}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Condições de saúde:</div>
                    {leadSelecionado.condicoes_saude?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.condicoes_saude.map((condicao: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{condicao}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhuma</div>
                    )}
                  </div>

                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Sintomas físicos respondidos como SIM:</div>
                    {leadSelecionado.respostas && Object.entries(leadSelecionado.respostas).filter(([key, valor]) => valor && CHAVES_SINTOMAS_FISICOS.includes(key)).length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {Object.entries(leadSelecionado.respostas)
                          .filter(([key, valor]) => valor && CHAVES_SINTOMAS_FISICOS.includes(key))
                          .map(([key]) => (
                            <li key={key} className="text-texto-escuro">{labelsPerguntas[key] || key}</li>
                          ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhum</div>
                    )}
                  </div>

                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Sentimentos emocionais respondidos como SIM:</div>
                    {leadSelecionado.respostas && Object.entries(leadSelecionado.respostas).filter(([key, valor]) => valor && CHAVES_SENTIMENTOS_EMOCIONAIS.includes(key)).length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {Object.entries(leadSelecionado.respostas)
                          .filter(([key, valor]) => valor && CHAVES_SENTIMENTOS_EMOCIONAIS.includes(key))
                          .map(([key]) => (
                            <li key={key} className="text-texto-escuro">{labelsPerguntas[key] || key}</li>
                          ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhum</div>
                    )}
                  </div>

                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Óleos essenciais indicados:</div>
                    {leadSelecionado.oleos_indicados?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.oleos_indicados.map((oleo: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{nomeExibicaoOleo(oleo)}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhum</div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Experiência com óleos essenciais:</div>
                      <div className="text-texto-escuro">{leadSelecionado.experiencia_oleos || 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Prontidão para começar:</div>
                      <div className="text-texto-escuro">{leadSelecionado.prontidao || 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Maior obstáculo:</div>
                      <div className="text-texto-escuro">{leadSelecionado.obstaculo || 'Não informado'}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Interesse declarado:</div>
                    {leadSelecionado.interesses?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.interesses.map((interesse: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{interesse}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Não informado</div>
                    )}
                  </div>

                  <div>
                    {leadSelecionado.prioridades?.length > 0 ? (
                      <>
                        <div className="text-sm text-texto-suave mb-2 font-medium">Prioridades selecionadas:</div>
                        <ul className="space-y-1">
                          {leadSelecionado.prioridades.map((p: string, i: number) => (
                            <li key={i} className="flex items-center gap-2 text-texto-escuro">
                              <span className="text-verde-escuro">✅</span> {p}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-texto-suave mb-2 font-medium">O que mais quer resolver na saúde:</div>
                        <div className="text-texto-escuro whitespace-pre-wrap bg-bege-fundo p-4 rounded-lg">
                          {leadSelecionado.objetivo_saude || 'Não informado'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <NotasObservacoes
                leadId={leadSelecionado._id}
                colecao="leads2"
                titulo="📝 Notas e Observações"
                notasIniciais={leadSelecionado.notasObservacoes || []}
                onNotasAtualizadas={(novas) => setLeadSelecionado({ ...leadSelecionado, notasObservacoes: novas })}
              />
              <div className="border-t-2 border-bege-medio my-8"></div>
              <ReferenciaRapidaOleos />
              <div className="border-t-2 border-bege-medio my-8"></div>
              <ScriptVendas lead={leadSelecionado} />
              <div className="border-t-2 border-bege-medio my-8"></div>
              <AdminModalTabs lead={leadSelecionado} />
              <div className="border-t-2 border-bege-medio my-8"></div>

              <div className="flex gap-3 pt-4 border-t border-bege-medio">
                <div className="flex-1 relative">
                  <select
                    value={leadSelecionado.status}
                    onChange={e => {
                      atualizarStatus(leadSelecionado._id, e.target.value)
                      setLeadSelecionado({ ...leadSelecionado, status: e.target.value })
                    }}
                    className="w-full appearance-none px-4 py-3 pr-10 rounded-xl text-base font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-verde-claro"
                    style={{
                      backgroundColor: obterCoresStatus(leadSelecionado.status).bg,
                      color: obterCoresStatus(leadSelecionado.status).text,
                    }}
                  >
                    <option value="Novo lead">Novo contato</option>
                    <option value="Em contato">Em contato</option>
                    <option value="Agendado">Agendado</option>
                    <option value="Convertido">Convertido</option>
                    <option value="Não respondeu">Não respondeu</option>
                    <option value="Não qualificado">Não qualificado</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={() => excluirLead(leadSelecionado._id)}
                  className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin2
